/**
 * CardEffects - System zastosowania efektów kart
 * Browser-compatible version (offline game engine)
 *
 * Odpowiedzialny za:
 * - Zastosowanie wszystkich 87 typów efektów z effectTypes.js
 * - Obsługa triggerów (ON_PLAY, ON_DRAW, WHILE_IN_HAND, etc.)
 * - Efekty wieloosobowe (ALL_PLAYERS_*, ALL_OTHER_PLAYERS_*)
 * - Efekty warunkowe (per ally, per discard, etc.)
 * - Efekty wyboru (CHOICE_*)
 */

import ResourceManager from './ResourceManager.js';
import GameEngine from './GameEngine.js';
import ArtifactSalvage from './ArtifactSalvage.js';
import { hasUnlimitedActions } from '../gameModeRules.js';

// Inlined EffectTypes (from backend/constants/effectTypes.js)
const EffectTypes = {
  // Artefakty
  ARTIFACT_REPLACEMENT_CHOICE: 'ARTIFACT_REPLACEMENT_CHOICE',
  ACTIVATE_EQUIPPED_ARTIFACT: 'ACTIVATE_EQUIPPED_ARTIFACT',
  EQUIP_ARTIFACT: 'EQUIP_ARTIFACT',
  LEGENDARY_ARTIFACT_ACQUISITION: 'LEGENDARY_ARTIFACT_ACQUISITION',

  // Zasoby
  GAIN_STRENGTH: 'GAIN_STRENGTH',
  GAIN_KNOWLEDGE: 'GAIN_KNOWLEDGE',
  GAIN_GOLD: 'GAIN_GOLD',
  GAIN_FAME: 'GAIN_FAME',
  DRAW_CARDS: 'DRAW_CARDS',
  EXTRA_PURCHASE: 'EXTRA_PURCHASE',
  NEXT_PURCHASE_DISCOUNT: 'NEXT_PURCHASE_DISCOUNT',
  NEXT_PURCHASE_DISCOUNT_PER_ALLY_IN_MARKET: 'NEXT_PURCHASE_DISCOUNT_PER_ALLY_IN_MARKET',

  // Tracenie zasobów
  LOSE_STRENGTH: 'LOSE_STRENGTH',
  LOSE_GOLD: 'LOSE_GOLD',
  LOSE_FAME: 'LOSE_FAME',
  LOSE_ALL_GOLD: 'LOSE_ALL_GOLD',
  LOSE_ALL_RESOURCES: 'LOSE_ALL_RESOURCES',
  LOSE_MAIN_ACTION: 'LOSE_MAIN_ACTION',

  // Dobieranie kart
  DRAW_TO_HAND_SIZE: 'DRAW_TO_HAND_SIZE',
  DRAW_LEGENDARY_ARTIFACT: 'DRAW_LEGENDARY_ARTIFACT',

  // Odrzucanie
  DISCARD_FROM_HAND: 'DISCARD_FROM_HAND',
  DISCARD_HAND: 'DISCARD_HAND',
  DISCARD_AND_DRAW: 'DISCARD_AND_DRAW',
  DISCARD_ARTIFACT: 'DISCARD_ARTIFACT',
  DISCARD_MARKET_CARD: 'DISCARD_MARKET_CARD',
  DISCARD_FROM_KNOWN_PATH: 'DISCARD_FROM_KNOWN_PATH',

  // Niszczenie
  DESTROY_FROM_HAND: 'DESTROY_FROM_HAND',
  DESTROY_FROM_DISCARD: 'DESTROY_FROM_DISCARD',
  DESTROY_FROM_HAND_OR_DISCARD: 'DESTROY_FROM_HAND_OR_DISCARD',
  DESTROY_FROM_HAND_OPTIONAL: 'DESTROY_FROM_HAND_OPTIONAL',
  DESTROY_WOUND_OR_CURSE: 'DESTROY_WOUND_OR_CURSE',
  DESTROY_OR_REMOVE_CURSE: 'DESTROY_OR_REMOVE_CURSE',
  DESTROY_ALL_CURSES: 'DESTROY_ALL_CURSES',
  DESTROY_CARD_FROM_HAND: 'DESTROY_CARD_FROM_HAND',
  DESTROY_ALLY_FROM_HAND: 'DESTROY_ALLY_FROM_HAND',
  DESTROY_MARKET_CARD: 'DESTROY_MARKET_CARD',
  DESTROY_EQUIPPED_ARTIFACT: 'DESTROY_EQUIPPED_ARTIFACT',
  OPTIONAL_DESTROY_FROM_HAND: 'OPTIONAL_DESTROY_FROM_HAND',
  BONUS_GAIN_GOLD_IF_DESTROYED: 'BONUS_GAIN_GOLD_IF_DESTROYED',

  // Wyzwania
  EXTRA_CHALLENGE: 'EXTRA_CHALLENGE',
  EXTRA_CHALLENGE_ON_MONSTER_DEFEAT: 'EXTRA_CHALLENGE_ON_MONSTER_DEFEAT',
  EXTRA_CHALLENGE_QUEST_OR_TRAP_ONLY: 'EXTRA_CHALLENGE_QUEST_OR_TRAP_ONLY',
  EXPEDITION_INTO_UNKNOWN_FREE_MAIN_ACTION: 'EXPEDITION_INTO_UNKNOWN_FREE_MAIN_ACTION',
  EXPEDITION_INTO_UNKNOWN_DOES_NOT_COUNT_LIMIT: 'EXPEDITION_INTO_UNKNOWN_DOES_NOT_COUNT_LIMIT',

  // Karty negatywne
  TAKE_WOUND: 'TAKE_WOUND',
  TAKE_CURSE: 'TAKE_CURSE',
  TAKE_SHAME: 'TAKE_SHAME',
  TAKE_WOUND_IF_NO_TROPHIES: 'TAKE_WOUND_IF_NO_TROPHIES',
  TAKE_WOUND_PER_MISSING_STRENGTH: 'TAKE_WOUND_PER_MISSING_STRENGTH',

  // Bazar
  REFRESH_MARKET: 'REFRESH_MARKET',
  REFRESH_MARKET_FULL: 'REFRESH_MARKET_FULL',
  CHAOS_MARKET: 'CHAOS_MARKET',
  MOVE_MARKET_CARD_TO_BOTTOM: 'MOVE_MARKET_CARD_TO_BOTTOM',
  REPLACE_ALL_MARKET_CARDS: 'REPLACE_ALL_MARKET_CARDS',
  TAKE_FREE_MARKET_CARD: 'TAKE_FREE_MARKET_CARD',
  TAKE_MARKET_CARD_COST_5_OR_LESS: 'TAKE_MARKET_CARD_COST_5_OR_LESS',
  NEXT_ARTIFACT_COSTS_4_GOLD_LESS: 'NEXT_ARTIFACT_COSTS_4_GOLD_LESS',

  // Znany szlak
  REFRESH_KNOWN_PATH: 'REFRESH_KNOWN_PATH',
  EXPAND_KNOWN_PATH: 'EXPAND_KNOWN_PATH',

  // Podglądanie
  PEEK_ADVENTURE_DECK: 'PEEK_ADVENTURE_DECK',
  PEEK_OWN_DECK_TOP: 'PEEK_OWN_DECK_TOP',
  PEEK_TOP_3_PLACE_IN_ORDER: 'PEEK_TOP_3_PLACE_IN_ORDER',

  // Przenoszenie kart
  PLACE_FROM_HAND_ON_TOP_OF_DECK: 'PLACE_FROM_HAND_ON_TOP_OF_DECK',
  PLACE_ALLY_ON_TOP_FROM_DISCARD: 'PLACE_ALLY_ON_TOP_FROM_DISCARD',
  RECOVER_MANEUVER_FROM_DISCARD: 'RECOVER_MANEUVER_FROM_DISCARD',
  TAKE_ALLY_FROM_MARKET_TO_TOP: 'TAKE_ALLY_FROM_MARKET_TO_TOP',
  SHUFFLE_HAND_INTO_DECK: 'SHUFFLE_HAND_INTO_DECK',

  // Wieloosobowe
  ALL_PLAYERS_DRAW: 'ALL_PLAYERS_DRAW',
  ALL_PLAYERS_GAIN_GOLD: 'ALL_PLAYERS_GAIN_GOLD',
  ALL_PLAYERS_GAIN_FAME: 'ALL_PLAYERS_GAIN_FAME',
  ALL_PLAYERS_GAIN_KNOWLEDGE: 'ALL_PLAYERS_GAIN_KNOWLEDGE',
  ALL_PLAYERS_LOSE_GOLD: 'ALL_PLAYERS_LOSE_GOLD',
  ALL_PLAYERS_LOSE_FAME: 'ALL_PLAYERS_LOSE_FAME',
  ALL_PLAYERS_DISCARD: 'ALL_PLAYERS_DISCARD',
  ALL_PLAYERS_TAKE_SHAME: 'ALL_PLAYERS_TAKE_SHAME',
  ALL_OTHER_PLAYERS_DRAW: 'ALL_OTHER_PLAYERS_DRAW',
  ALL_OTHER_PLAYERS_GAIN_GOLD: 'ALL_OTHER_PLAYERS_GAIN_GOLD',
  ALL_OTHER_PLAYERS_GAIN_STRENGTH: 'ALL_OTHER_PLAYERS_GAIN_STRENGTH',
  ALL_OTHER_PLAYERS_TAKE_WOUND: 'ALL_OTHER_PLAYERS_TAKE_WOUND',

  // Warunkowe
  GAIN_STRENGTH_PER_ALLY: 'GAIN_STRENGTH_PER_ALLY',
  GAIN_STRENGTH_PER_DISCARD: 'GAIN_STRENGTH_PER_DISCARD',
  GAIN_KNOWLEDGE_PER_DISCARDED_MANEUVER: 'GAIN_KNOWLEDGE_PER_DISCARDED_MANEUVER',
  GAIN_KNOWLEDGE_THIS_TURN: 'GAIN_KNOWLEDGE_THIS_TURN',
  GAIN_GOLD_ON_MONSTER_DEFEAT: 'GAIN_GOLD_ON_MONSTER_DEFEAT',
  GAIN_GOLD_PER_NEW_CARD: 'GAIN_GOLD_PER_NEW_CARD',
  GAIN_FAME_PER_DESTROYED: 'GAIN_FAME_PER_DESTROYED',

  // Wybór
  CHOICE_GAIN_GOLD_2_OR_GAIN_GOLD_3_TAKE_SHAME: 'CHOICE_GAIN_GOLD_2_OR_GAIN_GOLD_3_TAKE_SHAME',
  CHOICE_GAIN_STRENGTH_2_OR_GAIN_KNOWLEDGE_2: 'CHOICE_GAIN_STRENGTH_2_OR_GAIN_KNOWLEDGE_2',
  CHOICE_GAIN_STRENGTH_1_OR_GAIN_KNOWLEDGE_1: 'CHOICE_GAIN_STRENGTH_1_OR_GAIN_KNOWLEDGE_1',
  CHOICE_GAIN_STRENGTH_3_OR_GAIN_KNOWLEDGE_3: 'CHOICE_GAIN_STRENGTH_3_OR_GAIN_KNOWLEDGE_3',

  // Specjalne
  IGNORE_PENALTY_ON_FAILED_ADVENTURE: 'IGNORE_PENALTY_ON_FAILED_ADVENTURE',
  TAKE_FIRST_PLAYER_TOKEN: 'TAKE_FIRST_PLAYER_TOKEN',
  SKIP_NEXT_TURN: 'SKIP_NEXT_TURN',
  NEXT_TURN_ONE_MAIN_ACTION: 'NEXT_TURN_ONE_MAIN_ACTION',
  USELESS_CARD: 'USELESS_CARD',
  NONE: 'NONE',
  DISCARD_FROM_HAND_FOR_STRENGTH: 'DISCARD_FROM_HAND_FOR_STRENGTH',

  // Triggery
  ON_PLAY: 'ON_PLAY',
  ON_DRAW: 'ON_DRAW',
  WHILE_IN_HAND: 'WHILE_IN_HAND',
  UNTIL_NEXT_TURN: 'UNTIL_NEXT_TURN',
  PASSIVE: 'PASSIVE',
  ACTIVE: 'ACTIVE',

  // Koniec gry
  END_GAME_PENALTY: 'END_GAME_PENALTY',

  // Efekty artefaktów
  PEEK_AND_DISCARD_DECK: 'PEEK_AND_DISCARD_DECK',
  GAIN_RESOURCES: 'GAIN_RESOURCES',
  SCRY_ADVENTURE_DECK: 'SCRY_ADVENTURE_DECK',
  IMMUNITY: 'IMMUNITY',
  BONUS_KNOWLEDGE: 'BONUS_KNOWLEDGE',
  INCREASE_HAND_SIZE: 'INCREASE_HAND_SIZE',
  COPY_EFFECT: 'COPY_EFFECT',
  PAY_FAME_DRAW_CARDS: 'PAY_FAME_DRAW_CARDS',
  CONVERT_RESOURCES: 'CONVERT_RESOURCES',
  PLAY_FROM_DISCARD: 'PLAY_FROM_DISCARD',
  FREE_MARKET_CARD: 'FREE_MARKET_CARD',
  EXTRA_MAIN_ACTION: 'EXTRA_MAIN_ACTION',
  EXTRA_CHALLENGE_ACTION: 'EXTRA_CHALLENGE_ACTION',
  STAT_BONUS: 'STAT_BONUS',
  STRENGTH_PER_ARTIFACT: 'STRENGTH_PER_ARTIFACT',
  GAIN_GOLD_CONDITIONAL: 'GAIN_GOLD_CONDITIONAL',
  GAIN_GOLD_PER_TROPHY: 'GAIN_GOLD_PER_TROPHY',
  GAIN_ANY_RESOURCE: 'GAIN_ANY_RESOURCE',
  REPLACE_MARKET_CARD: 'REPLACE_MARKET_CARD',
  RECOVER_FROM_DISCARD: 'RECOVER_FROM_DISCARD',
  IGNORE_PENALTY: 'IGNORE_PENALTY',
  PAY_FOR_EXTRA_CHALLENGE: 'PAY_FOR_EXTRA_CHALLENGE',
  DRAW_CARD: 'DRAW_CARD',
  DESTROY_FOR_GOLD: 'DESTROY_FOR_GOLD',

  // Triggery artefaktów
  START_OF_ACTION_PHASE: 'START_OF_ACTION_PHASE',
  START_OF_TURN: 'START_OF_TURN',
  END_OF_TURN: 'END_OF_TURN',
  ON_MONSTER_DEFEAT: 'ON_MONSTER_DEFEAT',
  ON_ALLY_PLAY: 'ON_ALLY_PLAY',
  ON_CARD_DESTROY: 'ON_CARD_DESTROY',
  ON_KNOWLEDGE_CHALLENGE: 'ON_KNOWLEDGE_CHALLENGE',
  FIRST_ALLY_OR_MANEUVER: 'FIRST_ALLY_OR_MANEUVER',
  GAME_START: 'GAME_START',
  ONCE_PER_TURN: 'ONCE_PER_TURN',
  ON_PENALTY: 'ON_PENALTY',
  ALWAYS: 'ALWAYS'
};

// Poprawne ID kart negatywnych (zgodne z cards.json)
const NEGATIVE_CARD_IDS = {
  WOUND: 'negative_rana',
  SHAME: 'negative_wstyd',
  CURSES: ['curse_biedy', 'curse_zapomnienia', 'curse_paralizu', 'curse_ciezaru', 'curse_rozpaczy', 'curse_slabosci', 'curse_rozpusty', 'curse_erozji_umyslu', 'curse_ignorancji', 'curse_niezdarnosci', 'curse_niekompetencji', 'curse_biernosci']
};

/**
 * Klątwy odbierające Akcję Główną (Paraliżu, Bierności) — LUSTRO
 * `GameModeRules.MAIN_ACTION_CURSES`. W trybie bez budżetu Akcji nie robią nic poza
 * -2 pkt na koniec gry, więc wypadają z puli losowania.
 */
const MAIN_ACTION_CURSES = ['curse_paralizu', 'curse_biernosci'];

function getRandomCurse(game = null) {
  const all = NEGATIVE_CARD_IDS.CURSES;
  const pool = hasUnlimitedActions(game?.gameMode)
    ? all.filter((id) => !MAIN_ACTION_CURSES.includes(id))
    : all;
  const source = pool.length > 0 ? pool : all;
  return source[Math.floor(Math.random() * source.length)];
}

function isWoundCard(cardId) {
  return cardId === 'negative_rana' || cardId === 'wound';
}

function isCurseCard(cardId) {
  return cardId?.startsWith('curse_') || cardId === 'curse';
}

class CardEffects {
  /**
   * Zastosuj efekty karty
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @param {Object} card - Obiekt karty
   * @param {Object} context - Dodatkowy kontekst { choice?, target? }
   * @returns {Object} - { success: Boolean, appliedEffects: Array }
   */
  static async applyCardEffects(game, player, card, context = {}) {
    const appliedEffects = [];
    const effects = card.effects || [];

    for (const effect of effects) {
      try {
        const result = await this.applyEffect(game, player, effect, context);
        if (result.success) {
          appliedEffects.push({
            type: effect.type,
            value: effect.value,
            result: result.details
          });
        }
      } catch (error) {
        console.error(`Błąd zastosowania efektu ${effect.type}:`, error);
      }
    }

    return {
      success: true,
      appliedEffects
    };
  }

  /**
   * Zastosuj pojedynczy efekt
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @param {Object} effect - { type: String, value: Number|Object }
   * @param {Object} context - Dodatkowy kontekst
   * @returns {Object} - { success: Boolean, details: Object }
   */
  static async applyEffect(game, player, effect, context = {}) {
    const { type, value } = effect;

    console.log(`[CardEffects] Applying effect: ${type} with value:`, value);
    console.log(`[CardEffects] Player pool before:`, player.pool);

    // Obsługa anulowania/pominięcia wyboru
    if (context.cardId === null || context.choice === null || context.option === null) {
      console.log(`[CardEffects] Effect ${type} skipped by user (choice was null)`);
      return { success: true, details: { skipped: true } };
    }

    switch (type) {
      // ========================================
      // EFEKTY PODSTAWOWE - ZASOBY
      // ========================================

      // ADD_RESOURCES - uniwersalny efekt używany przez karty startowe
      // value jest obiektem: { strength?: number, knowledge?: number, gold?: number }
      case 'ADD_RESOURCES': {
        const added = {};
        if (value && typeof value === 'object') {
          if (value.strength) {
            ResourceManager.addToPool(player, { strength: value.strength });
            added.strength = value.strength;
          }
          if (value.knowledge) {
            ResourceManager.addToPool(player, { knowledge: value.knowledge });
            added.knowledge = value.knowledge;
          }
          if (value.gold) {
            ResourceManager.addToPool(player, { gold: value.gold });
            added.gold = value.gold;
          }
        }
        console.log(`[CardEffects] Player pool after ADD_RESOURCES:`, player.pool);
        return { success: true, details: { ...added, addedToPool: true } };
      }

      // GAIN_TOKEN - dodaje żeton specjalny (np. parts dla Mechanistycznej Kuźni)
      case 'GAIN_TOKEN': {
        const tokenType = effect.tokenType;

        console.log(`[CardEffects] GAIN_TOKEN processing: ${tokenType} +${value}`);

        // Auto-initialize tokens if missing
        if (!player.tokens) {
          console.log('[CardEffects] Initializing missing player.tokens object');
          player.tokens = { fury: 0, power: 0, parts: 0, knowledge: 0 };
        }

        if (tokenType) {
          // Update the token value
          const currentValue = player.tokens[tokenType] || 0;
          player.tokens[tokenType] = currentValue + value;

          // IMPORTANT: Mongoose detection for nested objects
          if (typeof player.markModified === 'function') {
            player.markModified('tokens');
          }

          console.log(`[CardEffects] SUCCESS: ${tokenType} is now ${player.tokens[tokenType]}`);
          return { success: true, details: { tokenType, value, addedToken: true, newValue: player.tokens[tokenType] } };
        }
        return { success: false, details: { reason: 'Token type not specified' } };
      }

      case EffectTypes.GAIN_STRENGTH:
        ResourceManager.addToPool(player, { strength: value });
        console.log(`[CardEffects] Player pool after GAIN_STRENGTH:`, player.pool);
        return { success: true, details: { strength: value, addedToPool: true } };

      case EffectTypes.GAIN_KNOWLEDGE:
        ResourceManager.addToPool(player, { knowledge: value });
        console.log(`[CardEffects] Player pool after GAIN_KNOWLEDGE:`, player.pool);
        return { success: true, details: { knowledge: value, addedToPool: true } };

      case EffectTypes.GAIN_GOLD:
        ResourceManager.addToPool(player, { gold: value });
        console.log(`[CardEffects] Player pool after GAIN_GOLD:`, player.pool);
        return { success: true, details: { gold: value, addedToPool: true } };

      case EffectTypes.GAIN_FAME:
        player.fame += value;
        return { success: true, details: { fame: value } };

      case EffectTypes.DRAW_CARDS:
        console.log(`[CardEffects] DRAW_CARDS: player.drawCards exists?`, typeof player.drawCards);
        if (player.drawCards) {
          const drawn = player.drawCards(value);
          console.log(`[CardEffects] DRAW_CARDS: drew ${drawn.length} cards:`, drawn);
          return { success: true, details: { drawn: drawn.length, cards: drawn } };
        }
        console.warn(`[CardEffects] DRAW_CARDS failed: drawCards method not available`);
        return { success: false, details: { reason: 'drawCards method not available' } };

      case EffectTypes.EXTRA_PURCHASE:
        player.extraPurchases = (player.extraPurchases || 0) + value;
        return { success: true, details: { extraPurchases: value } };

      case EffectTypes.NEXT_PURCHASE_DISCOUNT:
        player.nextPurchaseDiscount = (player.nextPurchaseDiscount || 0) + value;
        console.log(`[CardEffects] NEXT_PURCHASE_DISCOUNT: +${value} discount (total: ${player.nextPurchaseDiscount})`);
        return { success: true, details: { discount: value } };

      case EffectTypes.NEXT_PURCHASE_DISCOUNT_PER_ALLY_IN_MARKET: {
        const allyCount = (game.marketplace || []).filter(id => id.includes('ally')).length;
        const totalDiscount = allyCount * value;
        player.nextPurchaseDiscount = (player.nextPurchaseDiscount || 0) + totalDiscount;
        console.log(`[CardEffects] NEXT_PURCHASE_DISCOUNT_PER_ALLY_IN_MARKET: ${allyCount} allies × ${value} = ${totalDiscount} (total: ${player.nextPurchaseDiscount})`);
        return { success: true, details: { allyCount, discountPerAlly: value, totalDiscount } };
      }

      // ========================================
      // EFEKTY ARTEFAKTÓW
      // ========================================

      case 'ARTIFACT_REPLACEMENT_CHOICE': {
          const selectedId = context.cardId;
          const newCardId = effect.cardId;
          const slotKey = effect.value;  // np. 'pancerz', 'bron', 'amulet'
          const oldCardId = effect.options.find(id => id !== newCardId);

          // Inicjalizuj equippedArtifacts jeśli nie istnieje
          if (!player.equippedArtifacts) {
            player.equippedArtifacts = { pancerz: null, bron: null, amulet: null };
          }

          if (selectedId === newCardId) {
             // ZAMIEŃ (SWAP) - załóż nowy, zniszcz stary

             // 1. Zniszcz stary artefakt (usuń z gry)
             if (!player.destroyed) player.destroyed = [];
             player.destroyed.push(oldCardId);

             // 2. Usuń nowy z ręki
             const handIndex = player.hand.indexOf(newCardId);
             if (handIndex !== -1) player.hand.splice(handIndex, 1);

             // 3. Załóż nowy w slocie
             player.equippedArtifacts[slotKey] = newCardId;

             console.log(`[CardEffects] Swapped artifact in slot ${slotKey}: ${oldCardId} destroyed, ${newCardId} equipped`);
             return { success: true, details: { action: 'swap', slot: slotKey, equipped: newCardId, destroyed: oldCardId } };
          } else {
             // ODRZUĆ NOWY (KEEP OLD) - zniszcz nowy, zachowaj stary

             // 1. Usuń nowy z ręki
             const handIndex = player.hand.indexOf(newCardId);
             if (handIndex !== -1) player.hand.splice(handIndex, 1);

             // 2. Zniszcz nowy artefakt (usuń z gry)
             if (!player.destroyed) player.destroyed = [];
             player.destroyed.push(newCardId);

             // Stary pozostaje w slocie (już tam jest)

             console.log(`[CardEffects] Discarded new artifact: ${newCardId} destroyed, ${oldCardId} kept in slot ${slotKey}`);
             return { success: true, details: { action: 'discard_new', slot: slotKey, kept: oldCardId, destroyed: newCardId } };
          }
      }

      // ========================================
      // TRACENIE ZASOBÓW
      // ========================================

      // Utrata zasobu zabiera z PULI, a potem z BANKU, aż do zera — lustro backendu.
      case EffectTypes.LOSE_STRENGTH:
        return { success: true, details: { lost: ResourceManager.drainFromPoolAndBank(player, { strength: value }).strength || 0 } };

      case EffectTypes.LOSE_GOLD:
        return { success: true, details: { lost: ResourceManager.drainFromPoolAndBank(player, { gold: value }).gold || 0 } };

      case 'LOSE_KNOWLEDGE':
        return { success: true, details: { lost: ResourceManager.drainFromPoolAndBank(player, { knowledge: value }).knowledge || 0 } };

      case EffectTypes.LOSE_FAME:
        player.fame = Math.max(0, player.fame - value);
        return { success: true, details: { lost: value } };

      case EffectTypes.LOSE_ALL_GOLD: {
        const lostGoldBank = player.resources.gold;
        const lostGoldPool = player.pool?.gold || 0;
        player.resources.gold = 0;
        if (player.pool) player.pool.gold = 0;
        return { success: true, details: { lost: lostGoldBank + lostGoldPool } };
      }

      case EffectTypes.LOSE_ALL_RESOURCES:
        const lostRes = { ...player.resources };
        ResourceManager.resetResources(player);
        return { success: true, details: { lost: lostRes } };

      case EffectTypes.LOSE_MAIN_ACTION:
        if (player.mainActionsRemaining > 0) {
          player.mainActionsRemaining -= value;
          return { success: true, details: { lost: value } };
        }
        return { success: false, details: { reason: 'No actions to lose' } };

      // ========================================
      // DOBIERANIE KART
      // ========================================

      case EffectTypes.DRAW_TO_HAND_SIZE: {
        const handSizeLimit = value || player.handSize || 5;
        const toDraw = Math.max(0, handSizeLimit - player.hand.length);
        const drawnToSize = player.drawCards ? player.drawCards(toDraw) : [];
        return { success: true, details: { drawn: drawnToSize.length } };
      }

      // ========================================
      // ODRZUCANIE KART
      // ========================================

      case EffectTypes.DISCARD_FROM_HAND:
        // Gracz wybiera karty do odrzucenia
        // W kontekście powinien być { cardIds: [] } lub { cardId: "" }
        if (context.cardIds && Array.isArray(context.cardIds)) {
          const discarded = [];
          for (const cardId of context.cardIds) {
            const idx = player.hand.indexOf(cardId);
            if (idx !== -1) {
              player.hand.splice(idx, 1);
              player.discard.push(cardId);
              discarded.push(cardId);
            }
          }
          return { success: true, details: { discarded } };
        }
        if (context.cardId) {
          const idx = player.hand.indexOf(context.cardId);
          if (idx !== -1) {
            player.hand.splice(idx, 1);
            player.discard.push(context.cardId);
            return { success: true, details: { discarded: [context.cardId] } };
          }
        }
        return { success: false, details: { reason: 'No cardIds provided' } };

      case EffectTypes.DISCARD_HAND:
        if (player.discardHand) {
          const handSize = player.hand.length;
          player.discardHand();
          return { success: true, details: { discarded: handSize } };
        }
        return { success: false };

      case EffectTypes.DISCARD_AND_DRAW:
        // Odrzuć rękę i dobierz `value` kart
        if (player.discardHand && player.drawCards) {
          const discardedCount = player.hand.length;
          player.discardHand();
          const drawnCards = player.drawCards(value);
          return { success: true, details: { discarded: discardedCount, drawn: drawnCards.length } };
        }
        return { success: false };

      // ========================================
      // NISZCZENIE KART
      // ========================================

      case EffectTypes.DESTROY_FROM_HAND:
        // Gracz wybiera kartę z ręki do zniszczenia
        if (context.cardIds && Array.isArray(context.cardIds)) {
          const destroyedIds = [];
          for (const cId of context.cardIds) {
            if (player.destroyCard(cId, 'hand')) {
              destroyedIds.push(cId);
              player.cardsDestroyedThisTurn = (player.cardsDestroyedThisTurn || 0) + 1;
            }
          }
          return { success: true, details: { cardIds: destroyedIds } };
        }
        if (context.cardId && player.destroyCard) {
          const destroyed = player.destroyCard(context.cardId, 'hand');
          if (destroyed) player.cardsDestroyedThisTurn = (player.cardsDestroyedThisTurn || 0) + 1;
          return { success: destroyed, details: { cardId: context.cardId } };
        }
        return { success: false, details: { reason: 'No cardId provided' } };

      case EffectTypes.DESTROY_FROM_DISCARD:
        // Gracz wybiera kartę z odrzutu do zniszczenia
        if (context.cardIds && Array.isArray(context.cardIds)) {
          const destroyedIds = [];
          for (const cId of context.cardIds) {
            if (player.destroyCard(cId, 'discard')) {
              destroyedIds.push(cId);
              player.cardsDestroyedThisTurn = (player.cardsDestroyedThisTurn || 0) + 1;
            }
          }
          return { success: true, details: { cardIds: destroyedIds } };
        }
        if (context.cardId && player.destroyCard) {
          const destroyed = player.destroyCard(context.cardId, 'discard');
          if (destroyed) player.cardsDestroyedThisTurn = (player.cardsDestroyedThisTurn || 0) + 1;
          return { success: destroyed, details: { cardId: context.cardId } };
        }
        return { success: false };

      case EffectTypes.DESTROY_WOUND_OR_CURSE:
        // Zniszcz Ranę lub Klątwę (automatycznie)
        const woundOrCurse = [...player.hand, ...player.discard].find(cardId =>
          isWoundCard(cardId) || isCurseCard(cardId)
        );
        if (woundOrCurse && player.destroyCard) {
          player.destroyCard(woundOrCurse);
          return { success: true, details: { destroyed: woundOrCurse } };
        }
        return { success: false, details: { reason: 'No wound or curse found' } };

      case EffectTypes.DESTROY_ALL_CURSES:
        // Zniszcz wszystkie klątwy
        const cursesDestroyed = [];
        const allCards = [...player.hand, ...player.discard, ...player.deck];
        for (const cardId of allCards) {
          if (isCurseCard(cardId) && player.destroyCard) {
            player.destroyCard(cardId);
            cursesDestroyed.push(cardId);
          }
        }
        return { success: true, details: { destroyed: cursesDestroyed.length } };

      // ========================================
      // WYZWANIA
      // ========================================

      case EffectTypes.EXTRA_CHALLENGE:
        player.extraChallenges = (player.extraChallenges || 0) + value;
        return { success: true, details: { extraChallenges: value } };

      case EffectTypes.EXTRA_CHALLENGE_ON_MONSTER_DEFEAT:
        // Dodaj efekt tymczasowy
        if (player.addTemporaryEffect) {
          player.addTemporaryEffect(type, value, 'until_next_turn', game.currentTurn);
          return { success: true, details: { message: 'Extra challenge on next monster defeat' } };
        }
        return { success: false };

      // ========================================
      // KARTY NEGATYWNE
      // ========================================

      case EffectTypes.TAKE_WOUND:
        // Dodaj Ranę do odrzutu
        for (let i = 0; i < value; i++) {
          player.discard.push(NEGATIVE_CARD_IDS.WOUND);
        }
        return { success: true, details: { wounds: value } };

      case EffectTypes.TAKE_CURSE:
        // Dodaj losową Klątwę do odrzutu (pula zależy od trybu — patrz getRandomCurse)
        for (let i = 0; i < value; i++) {
          player.discard.push(getRandomCurse(game));
        }
        return { success: true, details: { curses: value } };

      case EffectTypes.TAKE_SHAME:
        // Dodaj Hańbę do odrzutu
        for (let i = 0; i < value; i++) {
          player.discard.push(NEGATIVE_CARD_IDS.SHAME);
        }
        return { success: true, details: { shame: value } };

      case EffectTypes.TAKE_WOUND_IF_NO_TROPHIES:
        // Weź Ranę jeśli brak trofeów
        if (player.trophies && player.trophies.length === 0) {
          player.discard.push(NEGATIVE_CARD_IDS.WOUND);
          return { success: true, details: { wound: true } };
        }
        return { success: true, details: { wound: false } };

      // ========================================
      // TARGOWISKO
      // ========================================

      case EffectTypes.REFRESH_MARKET:
        // Odśwież `value` kart na Targowisku
        for (let i = 0; i < value && game.marketplace.length > 0; i++) {
          const removed = game.marketplace.shift();
          game.marketDiscard.push(removed);
        }
        game.refillMarketplace();
        return { success: true, details: { refreshed: value } };

      case EffectTypes.REFRESH_MARKET_FULL:
        // Odśwież całe Bazar
        game.marketDiscard.push(...game.marketplace);
        game.marketplace = [];
        game.refillMarketplace();
        return { success: true, details: { refreshed: 5 } };

      case EffectTypes.TAKE_FREE_MARKET_CARD:
        // Weź darmową kartę z Targowiska
        // W kontekście powinien być { cardId }
        if (context.cardId) {
          const idx = game.marketplace.indexOf(context.cardId);
          if (idx !== -1) {
            game.marketplace[idx] = null;   // pusty slot zostaje na miejscu, refill wypełni go w tym samym polu
            player.discard.push(context.cardId);
            // Zwiększ licznik nabytych kart (dla efektów jak "Wiek Odkryć")
            player.cardsAcquiredThisTurn = (player.cardsAcquiredThisTurn || 0) + 1;
            game.refillMarketplace();
            return { success: true, details: { cardId: context.cardId, cardsAcquired: player.cardsAcquiredThisTurn } };
          }
        }
        return { success: false };

      // ========================================
      // WIELOOSOBOWE
      // ========================================

      case EffectTypes.ALL_PLAYERS_DRAW:
        // Wszyscy gracze dobierają
        for (const p of game.players) {
          if (p.drawCards) p.drawCards(value);
        }
        return { success: true, details: { players: game.players.length, cards: value } };

      case EffectTypes.ALL_PLAYERS_GAIN_GOLD:
        // Wszyscy gracze dostają złoto
        for (const p of game.players) {
          ResourceManager.addResources(p, { gold: value });
        }
        return { success: true, details: { players: game.players.length, gold: value } };

      case EffectTypes.ALL_OTHER_PLAYERS_TAKE_WOUND:
        // Wszyscy oprócz aktywnego gracza biorą Ranę
        for (const p of game.players) {
          if (p.userId.toString() !== player.userId.toString()) {
            p.discard.push(NEGATIVE_CARD_IDS.WOUND);
          }
        }
        return { success: true, details: { affected: game.players.length - 1 } };

      // ========================================
      // WARUNKOWE
      // ========================================

      case EffectTypes.GAIN_STRENGTH_PER_ALLY:
        // +1 Siła za każdego Sprzymierzeńca zagranego w tej turze (w player.played)
        const allies = (player.played || []).filter(cardId => {
          // Sprawdź czy to Sprzymierzeniec (cards.json type: "ally")
          return cardId.includes('ally');
        });
        const strengthGain = allies.length * (value || 1);
        ResourceManager.addToPool(player, { strength: strengthGain });
        return { success: true, details: { allies: allies.length, strength: strengthGain, addedToPool: true } };

      case EffectTypes.GAIN_GOLD_ON_MONSTER_DEFEAT:
        // Dodaj flagę bezpośrednio na graczu - złoto po pokonaniu potwora
        player.monsterDefeatBonusGold = (player.monsterDefeatBonusGold || 0) + (value || 1);

        // Obsługa wsteczna: Jeśli gracz już pokonał potwory w tej turze, otrzymaj złoto natychmiast
        // (Większość kart tego typu mówi "Jeśli pokonasz potwora w tej turze...", co sugeruje dowolną kolejność)
        if (player.monstersDefeatedThisTurn > 0) {
          const retroactiveGold = player.monstersDefeatedThisTurn * (value || 1);
          ResourceManager.addToPool(player, { gold: retroactiveGold });
          console.log(`[CardEffects] GAIN_GOLD_ON_MONSTER_DEFEAT: Przyznano wstecznie +${retroactiveGold} złota za ${player.monstersDefeatedThisTurn} pokonanych potworów`);
        }

        console.log(`[CardEffects] GAIN_GOLD_ON_MONSTER_DEFEAT: bonus set to ${player.monsterDefeatBonusGold}`);
        return { success: true, details: { message: 'Bonus gold on monster defeat', bonusGold: player.monsterDefeatBonusGold } };

      case EffectTypes.GAIN_FAME_PER_DESTROYED:
        // +1 Sława za każdą zniszczoną kartę w tej turze
        const destroyedCount = player.cardsDestroyedThisTurn || 0;
        const fameGain = destroyedCount * (value || 1);
        player.fame += fameGain;
        return { success: true, details: { destroyed: destroyedCount, fame: fameGain } };

      // ========================================
      // WYBORY
      // ========================================

      case EffectTypes.CHOICE_GAIN_GOLD_2_OR_GAIN_GOLD_3_TAKE_SHAME:
        // Gracz wybiera: +2 Złoto ALBO +3 Złoto + Hańba (do puli)
        if (context.choice === 'option1') {
          ResourceManager.addToPool(player, { gold: 2 });
          return { success: true, details: { choice: 'option1', gold: 2, addedToPool: true } };
        } else if (context.choice === 'option2') {
          ResourceManager.addToPool(player, { gold: 3 });
          player.discard.push(NEGATIVE_CARD_IDS.SHAME);
          return { success: true, details: { choice: 'option2', gold: 3, shame: 1, addedToPool: true } };
        }
        return { success: false, details: { reason: 'No choice provided' } };

      case EffectTypes.CHOICE_GAIN_STRENGTH_2_OR_GAIN_KNOWLEDGE_2:
        // Gracz wybiera: +2 Siła ALBO +2 Wiedza (do puli)
        if (context.choice === 'strength') {
          ResourceManager.addToPool(player, { strength: 2 });
          return { success: true, details: { choice: 'strength', strength: 2, addedToPool: true } };
        } else if (context.choice === 'knowledge') {
          ResourceManager.addToPool(player, { knowledge: 2 });
          return { success: true, details: { choice: 'knowledge', knowledge: 2, addedToPool: true } };
        }
        return { success: false, details: { reason: 'No choice provided' } };

      case 'ALL_PLAYERS_CHOICE_DISCARD_2_OR_TAKE_WOUND':
        // Gracz wybiera: Odrzuć 2 karty ALBO weź Ranę
        if (context.choice === 'discard_2') {
          // Walidacja: sprawdź czy gracz ma wystarczającą liczbę kart
          if (!player.hand || player.hand.length < 2) {
            // Gracz nie ma wystarczająco kart - automatycznie daj Ranę
            console.log(`[CardEffects] Gracz ma tylko ${player.hand?.length || 0} kart, wymuszam Ranę`);
            player.discard.push('negative_rana');
            return { success: true, details: { choice: 'discard_2_forced_wound', reason: 'insufficient_cards', added: 'negative_rana' } };
          }
          // Dodaj nowy pending effect dla odrzucenia 2 kart
          if (!player.pendingEffects) player.pendingEffects = [];
          player.pendingEffects.push({
            type: EffectTypes.DISCARD_FROM_HAND,
            value: 2,
            source: 'hand',
            options: [...player.hand], // Kopia tablicy dla bezpieczeństwa
            description: 'Wybierz dokładnie 2 karty do odrzucenia'
          });
          return { success: true, details: { choice: 'discard_2', nextStep: 'discard_from_hand' } };
        } else if (context.choice === 'take_wound') {
          // Dodaj Ranę
          player.discard.push('negative_rana');
          return { success: true, details: { choice: 'take_wound', added: 'negative_rana' } };
        }
        return { success: false, details: { reason: 'No choice provided for ALL_PLAYERS_CHOICE_DISCARD_2_OR_TAKE_WOUND' } };

      case 'CHOICE_GAIN_STRENGTH_1_OR_GAIN_KNOWLEDGE_1':
        // Gracz wybiera: +1 Siła ALBO +1 Wiedza (Znak Natury)
        if (context.choice === 'strength') {
          ResourceManager.addToPool(player, { strength: 1 });
          return { success: true, details: { choice: 'strength', strength: 1, addedToPool: true } };
        } else if (context.choice === 'knowledge') {
          ResourceManager.addToPool(player, { knowledge: 1 });
          return { success: true, details: { choice: 'knowledge', knowledge: 1, addedToPool: true } };
        }
        return { success: false, details: { reason: 'No choice provided' } };

      case 'CHOICE_GAIN_STRENGTH_3_OR_GAIN_KNOWLEDGE_3':
        // Gracz wybiera: +3 Siła ALBO +3 Wiedza (Znak Natury ulepszony?)
        if (context.choice === 'strength') {
          ResourceManager.addToPool(player, { strength: value || 3 });
          return { success: true, details: { choice: 'strength', strength: value || 3, addedToPool: true } };
        } else if (context.choice === 'knowledge') {
          ResourceManager.addToPool(player, { knowledge: value || 3 });
          return { success: true, details: { choice: 'knowledge', knowledge: value || 3, addedToPool: true } };
        }
        return { success: false, details: { reason: 'No choice provided' } };

      case 'CHOICE_GAIN_STRENGTH_8_OR_GOLD_8_OR_KNOWLEDGE_8':
        // Aspekt Bohatera - wybierz +8 Siły, +8 Złota, LUB +8 Wiedzy
        if (context.choice === 'strength') {
          ResourceManager.addToPool(player, { strength: value || 8 });
          return { success: true, details: { choice: 'strength', strength: value || 8, addedToPool: true } };
        } else if (context.choice === 'gold') {
          ResourceManager.addToPool(player, { gold: value || 8 });
          return { success: true, details: { choice: 'gold', gold: value || 8, addedToPool: true } };
        } else if (context.choice === 'knowledge') {
          ResourceManager.addToPool(player, { knowledge: value || 8 });
          return { success: true, details: { choice: 'knowledge', knowledge: value || 8, addedToPool: true } };
        }
        return { success: false, details: { reason: 'No choice provided' } };

      // ========================================
      // SPECJALNE
      // ========================================

      case EffectTypes.IGNORE_PENALTY_ON_FAILED_ADVENTURE:
        // Dodaj efekt tymczasowy
        if (player.addTemporaryEffect) {
          player.addTemporaryEffect(type, 1, 'until_next_turn', game.currentTurn);
          return { success: true, details: { message: 'Ignore penalty on next failed adventure' } };
        }
        return { success: false };

      case EffectTypes.TAKE_FIRST_PLAYER_TOKEN:
        // Gracz staje się pierwszym graczem w następnej turze
        game.firstPlayerToken = player.userId.toString();
        return { success: true, details: { message: 'First player token taken' } };

      case EffectTypes.SKIP_NEXT_TURN:
        // Gracz pomija następną turę
        player.skipNextTurn = true;
        return { success: true, details: { message: 'Next turn will be skipped' } };

      case EffectTypes.NEXT_TURN_ONE_MAIN_ACTION:
        player.nextTurnMaxMainActions = 1;
        return { success: true, details: { message: 'Next turn limited to 1 main action' } };

      case EffectTypes.NONE:
      case EffectTypes.USELESS_CARD:
        // Brak efektu
        return { success: true, details: { message: 'No effect' } };

      // ========================================
      // ODRZUCANIE KART - ROZSZERZONE
      // ========================================

      case EffectTypes.DISCARD_ARTIFACT:
        // Odrzuć artefakt z ręki
        if (context.cardId) {
          const idx = player.hand.indexOf(context.cardId);
          if (idx !== -1) {
            player.hand.splice(idx, 1);
            player.discard.push(context.cardId);
            return { success: true, details: { discarded: context.cardId } };
          }
        }
        return { success: false, details: { reason: 'No artifact cardId provided' } };

      case EffectTypes.DISCARD_MARKET_CARD:
        // Odrzuć kartę z Targowiska
        if (context.cardId && game.marketplace) {
          const idx = game.marketplace.indexOf(context.cardId);
          if (idx !== -1) {
            game.marketplace[idx] = null;   // pusty slot zostaje na miejscu, refill wypełni go w tym samym polu
            game.marketDiscard = game.marketDiscard || [];
            game.marketDiscard.push(context.cardId);
            if (game.refillMarketplace) game.refillMarketplace();
            return { success: true, details: { discarded: context.cardId } };
          }
        }
        return { success: false, details: { reason: 'No cardId provided or not in marketplace' } };

      case EffectTypes.DISCARD_FROM_KNOWN_PATH:
        // Odrzuć kartę ze Znanego Szlaku (Szlaku Mądrości lub Męstwa)
        if (context.cardId) {
          // Sprawdź Wisdom
          if (game.pathOfWisdom) {
            const idx = game.pathOfWisdom.indexOf(context.cardId);
            if (idx !== -1) {
              game.pathOfWisdom[idx] = null;   // pusty slot zostaje na miejscu (jak po pokonaniu)
              game.wisdomDiscard = game.wisdomDiscard || [];
              game.wisdomDiscard.push(context.cardId);
              return { success: true, details: { discarded: context.cardId, path: 'wisdom' } };
            }
          }
          // Sprawdź Courage
          if (game.pathOfCourage) {
            const idx = game.pathOfCourage.indexOf(context.cardId);
            if (idx !== -1) {
              game.pathOfCourage[idx] = null;   // pusty slot zostaje na miejscu (jak po pokonaniu)
              game.courageDiscard = game.courageDiscard || [];
              game.courageDiscard.push(context.cardId);
              return { success: true, details: { discarded: context.cardId, path: 'courage' } };
            }
          }
        }
        return { success: false, details: { reason: 'No cardId provided or not in any path' } };

      // ========================================
      // NISZCZENIE KART - ROZSZERZONE
      // ========================================

      case EffectTypes.DESTROY_FROM_HAND_OR_DISCARD:
        // Zniszcz kartę z ręki lub odrzutu (gracz wybiera)
        if (context.cardIds && Array.isArray(context.cardIds)) {
          const destroyedIds = [];
          for (const cId of context.cardIds) {
            let destroyed = false;
            if (player.destroyCard(cId, 'hand')) {
              destroyed = true;
            } else if (player.destroyCard(cId, 'discard')) {
              destroyed = true;
            }
            if (destroyed) {
              destroyedIds.push(cId);
              player.cardsDestroyedThisTurn = (player.cardsDestroyedThisTurn || 0) + 1;
            }
          }
          return { success: true, details: { cardIds: destroyedIds } };
        }
        if (context.cardId) {
          let destroyed = false;
          // Sprawdź rękę
          let idx = player.hand.indexOf(context.cardId);
          if (idx !== -1) {
            player.hand.splice(idx, 1);
            player.destroyed = player.destroyed || [];
            player.destroyed.push(context.cardId);
            destroyed = true;
          } else {
            // Sprawdź odrzut
            idx = player.discard.indexOf(context.cardId);
            if (idx !== -1) {
              player.discard.splice(idx, 1);
              player.destroyed = player.destroyed || [];
              player.destroyed.push(context.cardId);
              destroyed = true;
            }
          }
          if (destroyed) player.cardsDestroyedThisTurn = (player.cardsDestroyedThisTurn || 0) + 1;
          return { success: destroyed, details: { cardId: context.cardId } };
        }
        return { success: false, details: { reason: 'No cardId provided' } };

      case EffectTypes.DESTROY_FROM_HAND_OPTIONAL:
      case EffectTypes.OPTIONAL_DESTROY_FROM_HAND: {
        // Opcjonalne zniszczenie karty z ręki
        if (context.cardId) {
          const idx = player.hand.indexOf(context.cardId);
          if (idx !== -1) {
            player.hand.splice(idx, 1);
            player.destroyed = player.destroyed || [];
            player.destroyed.push(context.cardId);
            player.cardsDestroyedThisTurn = (player.cardsDestroyedThisTurn || 0) + 1;
            // Zapamiętaj koszt zniszczonej karty (dla BONUS_GAIN_GOLD_IF_DESTROYED)
            const isStarterCard = context.cardId.startsWith('starter_') || context.cardId.startsWith('negative_') || context.cardId.startsWith('curse_');
            let cardCostVal = 2;
            if (!isStarterCard) {
              const cardData = await GameEngine.getCardById(context.cardId);
              cardCostVal = cardData?.cost || 2;
            }
            player.lastDestroyedCardCost = cardCostVal;
            return { success: true, details: { cardId: context.cardId, destroyed: true, cardCost: cardCostVal } };
          }
        }
        // Opcjonalne - brak wyboru to też sukces
        return { success: true, details: { destroyed: false } };
      }

      case EffectTypes.DESTROY_OR_REMOVE_CURSE: {
        // Gracz wybiera Klątwę do zniszczenia z ręki, odrzutu lub talii (reshuffle po usunięciu z talii).
        if (!player.destroyed) player.destroyed = [];
        const chosenId = context.cardId || null;
        const removeFromZone = (zone, deckReshuffle = false) => {
          const arr = player[zone];
          if (!Array.isArray(arr)) return null;
          const idx = chosenId
            ? arr.findIndex(c => c === chosenId && isCurseCard(c))
            : arr.findIndex(c => isCurseCard(c));
          if (idx === -1) return null;
          const removed = arr.splice(idx, 1)[0];
          player.destroyed.push(removed);
          if (deckReshuffle) {
            for (let i = player.deck.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [player.deck[i], player.deck[j]] = [player.deck[j], player.deck[i]];
            }
          }
          return removed;
        };
        const order = context.source === 'discard'
          ? [['discard', false], ['hand', false], ['deck', true]]
          : context.source === 'deck'
            ? [['deck', true], ['hand', false], ['discard', false]]
            : [['hand', false], ['discard', false], ['deck', true]];
        for (const [zone, reshuffle] of order) {
          const removed = removeFromZone(zone, reshuffle);
          if (removed) {
            return { success: true, details: { removed, from: zone, deckShuffled: reshuffle } };
          }
        }
        return { success: false, details: { reason: 'No curse found' } };
      }

      case EffectTypes.DESTROY_CARD_FROM_HAND:
        // Zniszcz dowolną kartę z ręki
        if (context.cardId) {
          const idx = player.hand.indexOf(context.cardId);
          if (idx !== -1) {
            player.hand.splice(idx, 1);
            player.destroyed = player.destroyed || [];
            player.destroyed.push(context.cardId);
            return { success: true, details: { cardId: context.cardId } };
          }
        }
        return { success: false, details: { reason: 'Card not in hand' } };

      case EffectTypes.DESTROY_ALLY_FROM_HAND:
        // Zniszcz Sprzymierzeńca z ręki
        if (context.cardId && context.cardId.includes('ally')) {
          const idx = player.hand.indexOf(context.cardId);
          if (idx !== -1) {
            player.hand.splice(idx, 1);
            player.destroyed = player.destroyed || [];
            player.destroyed.push(context.cardId);
            return { success: true, details: { cardId: context.cardId } };
          }
        }
        return { success: false, details: { reason: 'Ally card not in hand' } };

      case EffectTypes.DESTROY_MARKET_CARD:
        // Zniszcz kartę z Targowiska
        if (context.cardId && game.marketplace) {
          const idx = game.marketplace.indexOf(context.cardId);
          if (idx !== -1) {
            game.marketplace[idx] = null;   // pusty slot zostaje na miejscu, refill wypełni go w tym samym polu
            // Zniszczone karty nie trafiają do odrzutu
            if (game.refillMarketplace) game.refillMarketplace();
            return { success: true, details: { cardId: context.cardId } };
          }
        }
        return { success: false, details: { reason: 'Card not in marketplace' } };

      case EffectTypes.DESTROY_EQUIPPED_ARTIFACT:
        // Zniszcz wyposażony artefakt (nowy slotowy system)
        if (player.equippedArtifacts) {
          // Znajdź artefakt po cardId lub weź pierwszy niepusty slot
          let slotToDestroy = null;
          let artifactId = context.cardId;

          if (artifactId) {
            // Znajdź slot po cardId
            for (const slot of ['pancerz', 'bron', 'amulet']) {
              if (player.equippedArtifacts[slot] === artifactId) {
                slotToDestroy = slot;
                break;
              }
            }
          } else {
            // Weź pierwszy niepusty slot
            for (const slot of ['pancerz', 'bron', 'amulet']) {
              if (player.equippedArtifacts[slot] !== null) {
                slotToDestroy = slot;
                artifactId = player.equippedArtifacts[slot];
                break;
              }
            }
          }

          if (slotToDestroy && artifactId) {
            player.equippedArtifacts[slotToDestroy] = null;
            // Ofiara z karty — bez rekompensaty za zniszczony Artefakt (lustro backendu).
            ArtifactSalvage.destroyWithoutSalvage(player, artifactId);
            return { success: true, details: { cardId: artifactId, slot: slotToDestroy } };
          }
        }
        return { success: false, details: { reason: 'No equipped artifact' } };

      case EffectTypes.BONUS_GAIN_GOLD_IF_DESTROYED: {
        // Bonus złoto równy kosztowi zniszczonej karty (Startowe = 2)
        if ((player.cardsDestroyedThisTurn || 0) > 0) {
          const goldToGive = player.lastDestroyedCardCost !== undefined && player.lastDestroyedCardCost !== null
            ? player.lastDestroyedCardCost
            : value;
          player.lastDestroyedCardCost = undefined;
          ResourceManager.addToPool(player, { gold: goldToGive });
          return { success: true, details: { gold: goldToGive, addedToPool: true } };
        }
        return { success: true, details: { gold: 0, reason: 'No cards destroyed this turn' } };
      }

      // ========================================
      // WYZWANIA I EKSPEDYCJE
      // ========================================

      case EffectTypes.EXTRA_CHALLENGE_QUEST_OR_TRAP_ONLY:
        // Dodatkowe wyzwanie tylko na Quest lub Pułapkę
        player.extraChallengeQuestOrTrap = (player.extraChallengeQuestOrTrap || 0) + value;
        return { success: true, details: { challenges: value } };

      case EffectTypes.EXPEDITION_INTO_UNKNOWN_FREE_MAIN_ACTION:
        // Ekspedycja w Nieznane nie kosztuje głównej akcji
        player.freeExpedition = true;
        return { success: true, details: { message: 'Next expedition is free' } };

      case EffectTypes.EXPEDITION_INTO_UNKNOWN_DOES_NOT_COUNT_LIMIT:
        // Ekspedycja nie liczy się do limitu
        player.expeditionDoesNotCountLimit = true;
        return { success: true, details: { message: 'Expedition does not count to limit' } };

      // ========================================
      // TARGOWISKO - ROZSZERZONE
      // ========================================

      case EffectTypes.CHAOS_MARKET:
        // Chaos na Targowisku - potasuj i odkryj nowe
        if (game.marketplace && game.marketDeck) {
          game.marketDiscard = game.marketDiscard || [];
          game.marketDiscard.push(...game.marketplace);
          game.marketplace = [];
          // Potasuj odrzut do talii
          game.marketDeck.push(...game.marketDiscard);
          game.marketDiscard = [];
          this.shuffleArray(game.marketDeck);
          if (game.refillMarketplace) game.refillMarketplace();
          return { success: true, details: { message: 'Marketplace reshuffled' } };
        }
        return { success: false };

      case EffectTypes.MOVE_MARKET_CARD_TO_BOTTOM:
        // Przenieś kartę z Targowiska na spód talii
        if (context.cardId && game.marketplace && game.marketDeck) {
          const idx = game.marketplace.indexOf(context.cardId);
          if (idx !== -1) {
            game.marketplace[idx] = null;   // pusty slot zostaje na miejscu, refill wypełni go w tym samym polu
            game.marketDeck.push(context.cardId);
            if (game.refillMarketplace) game.refillMarketplace();
            return { success: true, details: { cardId: context.cardId } };
          }
        }
        return { success: false, details: { reason: 'Card not in marketplace' } };

      case EffectTypes.REPLACE_ALL_MARKET_CARDS:
        // Wymień wszystkie karty na Targowisku (opcjonalne - "Możesz")
        if (context.choice === 'skip' || context.option === 'skip') {
          return { success: true, details: { skipped: true } };
        }
        if (context.choice === 'replace' || context.option === 'replace') {
          if (game.marketplace && game.marketDeck) {
            const count = game.marketplace.length;
            game.marketDiscard = game.marketDiscard || [];
            game.marketDiscard.push(...game.marketplace);
            game.marketplace = [];
            if (game.refillMarketplace) game.refillMarketplace();
            return { success: true, details: { replaced: count } };
          }
        }
        return { success: false, details: { reason: 'No choice provided' } };

      case EffectTypes.TAKE_MARKET_CARD_COST_5_OR_LESS:
        // Weź darmowo kartę z Targowiska o koszcie ≤5
        if (context.cardId && game.marketplace) {
          const idx = game.marketplace.indexOf(context.cardId);
          if (idx !== -1) {
            // TODO: Sprawdź koszt karty (wymaga dostępu do definicji karty)
            game.marketplace[idx] = null;   // pusty slot zostaje na miejscu, refill wypełni go w tym samym polu
            player.discard.push(context.cardId);
            if (game.refillMarketplace) game.refillMarketplace();
            return { success: true, details: { cardId: context.cardId } };
          }
        }
        return { success: false, details: { reason: 'Card not available or cost > 5' } };

      case EffectTypes.NEXT_ARTIFACT_COSTS_4_GOLD_LESS:
        // Następny artefakt kosztuje 4 złoto mniej
        player.artifactDiscount = (player.artifactDiscount || 0) + 4;
        return { success: true, details: { discount: 4 } };

      // ========================================
      // ZNANY SZLAK
      // ========================================

      case EffectTypes.REFRESH_KNOWN_PATH:
        // Odśwież OBA szlaki (Zmiana Ścieżek) - oprócz Bossów/Epickich Zadań/Pułapek
        const refreshedPaths = { wisdom: 0, courage: 0, skippedBosses: [] };

        // Zapamiętaj stary limit
        const oldLimit = game.expandedPathLimit || 3;

        // Zmiana Ścieżek (REFRESH_KNOWN_PATH) - respektujemy ustawienia gry
        const pathLimit = game.gameConfig?.pathSize || 3;
        game.expandedPathLimit = Math.max(oldLimit, pathLimit);

        // Odśwież Szlak Mądrości (bez epic_quest i trap)
        if (game.pathOfWisdom) {
          game.wisdomDiscard = game.wisdomDiscard || [];
          const keptCards = [];
          for (const cardId of game.pathOfWisdom) {
            // Pozostaw epickie zadania i pułapki
            if (cardId.includes('epic_quest') || cardId.includes('trap')) {
              keptCards.push(cardId);
              refreshedPaths.skippedBosses.push(cardId);
            } else {
              game.wisdomDiscard.push(cardId);
            }
          }
          game.pathOfWisdom = [...keptCards];

          // Uzupełnij używając metody modelu (obsługuje przetasowanie)
          if (game.refillPathOfWisdom) {
            game.refillPathOfWisdom();
          }
          refreshedPaths.wisdom = game.pathOfWisdom.length;
        }

        // Odśwież Szlak Odwagi (bez bossów)
        if (game.pathOfCourage) {
          game.courageDiscard = game.courageDiscard || [];
          const keptCards = [];
          for (const cardId of game.pathOfCourage) {
            if (cardId.includes('boss') || cardId.includes('epic_boss')) {
              keptCards.push(cardId);
              refreshedPaths.skippedBosses.push(cardId);
            } else {
              game.courageDiscard.push(cardId);
            }
          }
          game.pathOfCourage = [...keptCards];

          // Uzupełnij używając metody modelu
          if (game.refillPathOfCourage) {
            game.refillPathOfCourage();
          }
          refreshedPaths.courage = game.pathOfCourage.length;
        }

        // Zapisz zmiany w modelu (guard for non-Mongoose objects)
        if (typeof game.markModified === 'function') {
          game.markModified('pathOfWisdom');
          game.markModified('pathOfCourage');
          game.markModified('expandedPathLimit');
          game.markModified('wisdomDiscard');
          game.markModified('courageDiscard');
        }

        return { success: true, details: { refreshed: refreshedPaths } };

      case EffectTypes.EXPAND_KNOWN_PATH:
        // Rozszerz oba szlaki o `value` kart (Zapomniany Grobowiec)
        // Zwiększa limit szlaków do 6 kart (3+value dla każdego)
        const expandResult = { wisdomAdded: [], courageAdded: [] };

        // Zwiększ limit szlaku Mądrości
        if (game.pathOfWisdom && game.wisdomDeck) {
          for (let i = 0; i < value && game.wisdomDeck.length > 0; i++) {
            const card = game.wisdomDeck.shift();
            if (!card.startsWith('event_')) {
              game.pathOfWisdom.push(card);
              expandResult.wisdomAdded.push(card);
            } else {
              game.wisdomDiscard = game.wisdomDiscard || [];
              game.wisdomDiscard.push(card);
              i--; // Spróbuj ponownie
            }
          }
        }

        // Zwiększ limit szlaku Odwagi
        if (game.pathOfCourage && game.courageDeck) {
          for (let i = 0; i < value && game.courageDeck.length > 0; i++) {
            const card = game.courageDeck.shift();
            if (!card.startsWith('event_')) {
              game.pathOfCourage.push(card);
              expandResult.courageAdded.push(card);
            } else {
              game.courageDiscard = game.courageDiscard || [];
              game.courageDiscard.push(card);
              i--; // Spróbuj ponownie
            }
          }
        }

        // Ustaw flagę rozszerzonego szlaku (dla logiki uzupełniania)
        game.expandedPathLimit = (game.expandedPathLimit || 3) + value;

        return {
          success: true,
          details: {
            wisdomAdded: expandResult.wisdomAdded.length,
            courageAdded: expandResult.courageAdded.length,
            newPathLimit: game.expandedPathLimit
          }
        };

      // ========================================
      // PODGLĄDANIE KART
      // ========================================

      case EffectTypes.PEEK_ADVENTURE_DECK: {
        // Faza 1: gracz wybrał ścieżkę - utwórz jeden grupowy efekt fazy 2
        const rawPath = context.choice || context.option || context.path;
        const pathChosen = typeof rawPath === 'string' ? rawPath : (rawPath?.option || 'wisdom');
        const deck = pathChosen === 'courage' ? game.courageDeck : game.wisdomDeck;
        if (!deck || deck.length === 0) {
          return { success: true, details: { skipped: true, reason: 'empty_deck', path: pathChosen } };
        }
        const peekCount = Math.min(value || 1, deck.length);
        const peekedCardIds = deck.slice(0, peekCount);
        if (!player.pendingEffects) player.pendingEffects = [];

        player.pendingEffects.push({
          type: 'PEEK_ADVENTURE_DECK_RESOLVE',
          value: peekCount,
          source: 'choice',
          options: peekedCardIds,
          description: `Podejrzyj ${peekCount} wierzchnich kart talii ${pathChosen === 'courage' ? 'Męstwa' : 'Mądrości'}`,
          cardId: effect.cardId || null,
          metadata: { path: pathChosen, topCardIds: peekedCardIds, totalCards: peekCount }
        });

        return { success: true, details: { path: pathChosen, peekedCards: peekedCardIds, phase: 1 } };
      }

      case 'PEEK_ADVENTURE_DECK_RESOLVE': {
        // Faza 2: gracz wybrał decyzje dla podejrzanych kart
        // context.choice może być obiektem { decisions: { cardId: 'keep'|'bottom' } }
        const rawDecision = context.choice || context.option;
        const decisions = (typeof rawDecision === 'object' && rawDecision?.decisions) ? rawDecision.decisions : {};
        const metadata = effect.metadata || {};
        const pathType = metadata.path || 'wisdom';
        const topCardIds = metadata.topCardIds || (metadata.topCardId ? [metadata.topCardId] : []);
        const deck = pathType === 'courage' ? game.courageDeck : game.wisdomDeck;

        if (!deck || topCardIds.length === 0) return { success: true, details: { skipped: true } };

        const cardsToKeep = [];
        const cardsToBottom = [];

        for (const cardId of topCardIds) {
          const idx = deck.indexOf(cardId);
          if (idx !== -1) {
            deck.splice(idx, 1);
            if (decisions[cardId] === 'bottom') {
              cardsToBottom.push(cardId);
            } else {
              cardsToKeep.push(cardId);
            }
          }
        }

        deck.unshift(...cardsToKeep);
        deck.push(...cardsToBottom);

        if (pathType === 'courage') {
          game.courageDeck = deck;
        } else {
          game.wisdomDeck = deck;
        }

        return { success: true, details: { kept: cardsToKeep, movedToBottom: cardsToBottom, path: pathType } };
      }

      case EffectTypes.PEEK_OWN_DECK_TOP:
        // Podejrzyj `value` kart z wierzchu własnej talii
        if (player.deck) {
          const peeked = player.deck.slice(0, value);
          return { success: true, details: { cards: peeked, count: peeked.length } };
        }
        return { success: false };

      case EffectTypes.PEEK_TOP_3_PLACE_IN_ORDER:
        // Podejrzyj 3 karty i ułóż w dowolnej kolejności
        if (player.deck && context.order && Array.isArray(context.order)) {
          const top3 = player.deck.splice(0, 3);
          // Ułóż w kolejności podanej przez gracza
          for (const cardId of context.order.reverse()) {
            if (top3.includes(cardId)) {
              player.deck.unshift(cardId);
            }
          }
          return { success: true, details: { arranged: context.order } };
        }
        // Jeśli brak kontekstu, zwróć karty do podejrzenia
        if (player.deck) {
          const peeked = player.deck.slice(0, 3);
          return { success: true, details: { cards: peeked, needsOrder: true } };
        }
        return { success: false };

      // ========================================
      // PRZENOSZENIE KART
      // ========================================

      case EffectTypes.PLACE_FROM_HAND_ON_TOP_OF_DECK:
        // Połóż kartę z ręki na wierzch talii
        if (context.cardId) {
          const idx = player.hand.indexOf(context.cardId);
          if (idx !== -1) {
            player.hand.splice(idx, 1);
            player.deck.unshift(context.cardId);
            return { success: true, details: { cardId: context.cardId } };
          }
        }
        return { success: false, details: { reason: 'Card not in hand' } };

      case EffectTypes.PLACE_ALLY_ON_TOP_FROM_DISCARD:
        // Połóż Sprzymierzeńca z odrzutu na wierzch talii
        if (context.cardId && context.cardId.includes('ally')) {
          const idx = player.discard.indexOf(context.cardId);
          if (idx !== -1) {
            player.discard.splice(idx, 1);
            player.deck.unshift(context.cardId);
            return { success: true, details: { cardId: context.cardId } };
          }
        }
        return { success: false, details: { reason: 'Ally not in discard' } };

      case EffectTypes.RECOVER_MANEUVER_FROM_DISCARD:
        // Odzyskaj Manewr z odrzutu do ręki
        if (context.cardId && context.cardId.includes('maneuver')) {
          const idx = player.discard.indexOf(context.cardId);
          if (idx !== -1) {
            player.discard.splice(idx, 1);
            player.hand.push(context.cardId);
            return { success: true, details: { cardId: context.cardId } };
          }
        }
        return { success: false, details: { reason: 'Maneuver not in discard' } };

      case EffectTypes.TAKE_ALLY_FROM_MARKET_TO_TOP:
        // Weź Sprzymierzeńca z Targowiska na wierzch talii
        if (context.cardId && game.marketplace) {
          const idx = game.marketplace.indexOf(context.cardId);
          if (idx !== -1 && context.cardId.includes('ally')) {
            game.marketplace[idx] = null;   // pusty slot zostaje na miejscu, refill wypełni go w tym samym polu
            player.deck.unshift(context.cardId);
            if (game.refillMarketplace) game.refillMarketplace();
            return { success: true, details: { cardId: context.cardId } };
          }
        }
        return { success: false, details: { reason: 'Ally not in marketplace' } };

      case EffectTypes.SHUFFLE_HAND_INTO_DECK:
        // Wtasuj rękę do talii
        if (player.hand.length > 0) {
          const handSize = player.hand.length;
          player.deck.push(...player.hand);
          player.hand = [];
          this.shuffleArray(player.deck);
          return { success: true, details: { shuffled: handSize } };
        }
        return { success: true, details: { shuffled: 0 } };

      // ========================================
      // WIELOOSOBOWE - ROZSZERZONE
      // ========================================

      case EffectTypes.ALL_PLAYERS_GAIN_FAME:
        for (const p of game.players) {
          p.fame += value;
        }
        return { success: true, details: { players: game.players.length, fame: value } };

      case EffectTypes.ALL_PLAYERS_GAIN_KNOWLEDGE:
        for (const p of game.players) {
          ResourceManager.addResources(p, { knowledge: value });
        }
        return { success: true, details: { players: game.players.length, knowledge: value } };

      case EffectTypes.ALL_PLAYERS_LOSE_GOLD:
        for (const p of game.players) {
          p.resources.gold = Math.max(0, p.resources.gold - value);
        }
        return { success: true, details: { players: game.players.length, lost: value } };

      case EffectTypes.ALL_PLAYERS_LOSE_FAME:
        for (const p of game.players) {
          p.fame = Math.max(0, p.fame - value);
        }
        return { success: true, details: { players: game.players.length, lost: value } };

      case EffectTypes.ALL_PLAYERS_DISCARD:
        // Wszyscy gracze odrzucają `value` kart
        // Obsługa warunku IF_HAND_SIZE_3_OR_MORE (Bandycka Zasadzka)
        const discardCondition = effect.condition;
        const affectedPlayers = [];

        for (const p of game.players) {
          // Sprawdź warunek jeśli istnieje
          if (discardCondition === 'IF_HAND_SIZE_3_OR_MORE') {
            if (p.hand.length < 3) {
              console.log(`[ALL_PLAYERS_DISCARD] Gracz ${p.username} ma ${p.hand.length} kart - pominięty (wymaga 3+)`);
              continue;
            }
          }

          for (let i = 0; i < value && p.hand.length > 0; i++) {
            // Losowe odrzucenie jeśli brak wyboru
            const randomIdx = Math.floor(Math.random() * p.hand.length);
            const discarded = p.hand.splice(randomIdx, 1)[0];
            p.discard.push(discarded);
          }
          affectedPlayers.push(p.username);
        }
        return { success: true, details: { players: affectedPlayers.length, affectedPlayers, discarded: value, condition: discardCondition } };

      case EffectTypes.ALL_PLAYERS_TAKE_SHAME:
        for (const p of game.players) {
          p.discard.push(NEGATIVE_CARD_IDS.SHAME);
        }
        return { success: true, details: { players: game.players.length } };

      case EffectTypes.ALL_OTHER_PLAYERS_DRAW:
        for (const p of game.players) {
          if (p.userId.toString() !== player.userId.toString() && p.drawCards) {
            p.drawCards(value);
          }
        }
        return { success: true, details: { affected: game.players.length - 1, cards: value } };

      case EffectTypes.ALL_OTHER_PLAYERS_GAIN_GOLD:
        for (const p of game.players) {
          if (p.userId.toString() !== player.userId.toString()) {
            ResourceManager.addResources(p, { gold: value });
          }
        }
        return { success: true, details: { affected: game.players.length - 1, gold: value } };

      case EffectTypes.ALL_OTHER_PLAYERS_GAIN_STRENGTH:
        for (const p of game.players) {
          if (p.userId.toString() !== player.userId.toString()) {
            ResourceManager.addResources(p, { strength: value });
          }
        }
        return { success: true, details: { affected: game.players.length - 1, strength: value } };

      // ========================================
      // WARUNKOWE - ROZSZERZONE
      // ========================================

      case EffectTypes.TAKE_WOUND_PER_MISSING_STRENGTH:
        // Weź Ranę za każdy brakujący punkt Siły
        const requiredStr = value || 0;
        const missing = Math.max(0, requiredStr - player.resources.strength);
        for (let i = 0; i < missing; i++) {
          player.discard.push(NEGATIVE_CARD_IDS.WOUND);
        }
        return { success: true, details: { wounds: missing, required: requiredStr } };

      case EffectTypes.GAIN_STRENGTH_PER_DISCARD: {
        // +1 Siła za każdą kartę w stosie kart odrzuconych
        const discardPileSize = player.discard ? player.discard.length : 0;
        const strGain = discardPileSize * (value || 1);
        ResourceManager.addToPool(player, { strength: strGain });
        console.log(`[CardEffects] GAIN_STRENGTH_PER_DISCARD: ${discardPileSize} cards in discard = +${strGain} Strength`);
        return { success: true, details: { discardCount: discardPileSize, strength: strGain, addedToPool: true } };
      }

      case EffectTypes.DISCARD_FROM_HAND_FOR_STRENGTH: {
        // Ścieżka Wojny: odrzuć wybrane karty z ręki, zyskaj +value Siły za każdą
        const cardIds = context.cardIds || (context.cardId ? [context.cardId] : []);
        const discarded = [];
        for (const cid of cardIds) {
          const idx = player.hand.indexOf(cid);
          if (idx !== -1) {
            player.hand.splice(idx, 1);
            player.discard.push(cid);
            discarded.push(cid);
          }
        }
        const strengthGained = discarded.length * (value || 2);
        if (strengthGained > 0) {
          ResourceManager.addToPool(player, { strength: strengthGained });
        }
        return { success: true, details: { discarded, strength: strengthGained, addedToPool: true } };
      }

      case EffectTypes.GAIN_KNOWLEDGE_PER_DISCARDED_MANEUVER:
        // +1 Wiedza za każdy odrzucony Manewr
        const discardedManeuvers = (player.discardedManeuversThisTurn || 0);
        const knowGain = discardedManeuvers * (value || 1);
        ResourceManager.addResources(player, { knowledge: knowGain });
        return { success: true, details: { maneuvers: discardedManeuvers, knowledge: knowGain } };

      case EffectTypes.GAIN_KNOWLEDGE_THIS_TURN:
        // Zyskaj Wiedzę do puli (tymczasowe, na tę turę)
        ResourceManager.addToPool(player, { knowledge: value });
        console.log(`[CardEffects] GAIN_KNOWLEDGE_THIS_TURN: +${value} Knowledge to pool`);
        return { success: true, details: { knowledge: value, temporary: true, addedToPool: true } };

      case 'GAIN_2_STRENGTH_OR_2_KNOWLEDGE_TEMPORARY': {
        const choice = context.choice || context.option;
        if (choice === 'strength') {
          ResourceManager.addToPool(player, { strength: 2 });
          player.temporaryStrength = (player.temporaryStrength || 0) + 2;
          return { success: true, details: { strength: 2, temporary: true, addedToPool: true } };
        } else if (choice === 'knowledge') {
          ResourceManager.addToPool(player, { knowledge: 2 });
          player.temporaryKnowledge = (player.temporaryKnowledge || 0) + 2;
          return { success: true, details: { knowledge: 2, temporary: true, addedToPool: true } };
        }
        return { success: false, details: { reason: 'No choice provided for GAIN_2_STRENGTH_OR_2_KNOWLEDGE_TEMPORARY' } };
      }

      case EffectTypes.GAIN_GOLD_PER_NEW_CARD:
        // +1 Złoto za każdą nowo nabytą kartę w tej turze (Wiek Odkryć)
        const newCards = player.cardsAcquiredThisTurn || 0;
        const goldGain = newCards * (value || 1);
        ResourceManager.addToPool(player, { gold: goldGain });
        console.log(`[CardEffects] GAIN_GOLD_PER_NEW_CARD: ${newCards} new cards = +${goldGain} Gold to pool`);
        return { success: true, details: { newCards, gold: goldGain, addedToPool: true } };

      case EffectTypes.DRAW_LEGENDARY_ARTIFACT: {
        // Dobierz Legendarny Artefakt - wymusza natychmiastową decyzję
        if (!game.legendaryArtifactsDeck || game.legendaryArtifactsDeck.length === 0) {
          return { success: false, details: { reason: 'No legendary artifacts available' } };
        }

        const artifactId = game.legendaryArtifactsDeck.shift();

        // Pobierz dane karty aby znać kategorię (slot)
        const artifactCard = await GameEngine.getCardById(artifactId);

        if (!artifactCard) {
          return { success: false, details: { reason: 'Artifact card not found' } };
        }

        // Mapuj kategorię na slot
        const categoryToSlot = { 'Pancerz': 'pancerz', 'Broń': 'bron', 'Amulet': 'amulet', 'Legendarny Pancerz': 'pancerz', 'Legendarna Broń': 'bron', 'Legendarny Amulet': 'amulet' };
        const slotKey = categoryToSlot[artifactCard.category] || 'amulet';

        // Inicjalizuj equippedArtifacts jeśli brak
        if (!player.equippedArtifacts) {
          player.equippedArtifacts = { pancerz: null, bron: null, amulet: null };
        }

        const currentArtifactId = player.equippedArtifacts[slotKey];

        // Dodaj pending effect wymagający natychmiastowej decyzji
        if (!player.pendingEffects) player.pendingEffects = [];

        player.pendingEffects.unshift({
          type: 'LEGENDARY_ARTIFACT_ACQUISITION',
          cardId: artifactId,
          value: slotKey,                      // slot key
          currentArtifact: currentArtifactId,  // null lub ID obecnego artefaktu
          mandatory: true,
          source: 'legendary_reward'
        });

        console.log(`[DRAW_LEGENDARY_ARTIFACT] Pending acquisition: ${artifactCard.name} (slot: ${slotKey})`);

        return {
          success: true,
          details: {
            artifact: artifactId,
            artifactName: artifactCard.name,
            slot: slotKey,
            hasConflict: !!currentArtifactId,
            needsDecision: true
          }
        };
      }

      case 'LEGENDARY_ARTIFACT_ACQUISITION':
      case EffectTypes.LEGENDARY_ARTIFACT_ACQUISITION: {
        const artifactId = effect.cardId;
        const slotKey = effect.value;
        const choice = context.choice; // 'equip', 'destroy', 'replace', 'keep_current'

        // Inicjalizuj jeśli brak
        if (!player.equippedArtifacts) {
          player.equippedArtifacts = { pancerz: null, bron: null, amulet: null };
        }
        if (!player.destroyed) player.destroyed = [];

        // Czytaj AKTUALNEGO okupanta slotu (na żywo), nie wartość z dobrania — seria
        // legendarnych nagród na ten sam slot mogła go już zająć. Inaczej 'equip' po cichu
        // nadpisywał założony artefakt (np. Całun Wskrzesiciela "znikał" bez śladu).
        const liveOccupant = player.equippedArtifacts[slotKey] || null;

        switch (choice) {
          // Załóż nowy. Zajęty slot = zamiana: stary do zniszczonych (nigdy nie gubimy go po cichu).
          case 'equip':
          case 'replace':
            if (liveOccupant && liveOccupant !== artifactId) {
              player.destroyed.push(liveOccupant);
              console.log(`[LEGENDARY] Slot ${slotKey} zajęty przez ${liveOccupant} — zniszczono przed założeniem ${artifactId}`);
            }
            player.equippedArtifacts[slotKey] = artifactId;
            console.log(`[LEGENDARY] Equipped ${artifactId} in ${slotKey}${liveOccupant ? ` (replaced ${liveOccupant})` : ''}`);
            return {
              success: true,
              details: {
                action: liveOccupant && liveOccupant !== artifactId ? 'replaced' : 'equipped',
                equipped: artifactId,
                destroyed: liveOccupant && liveOccupant !== artifactId ? liveOccupant : undefined,
                slot: slotKey
              }
            };

          // Odrzuć nowy — zachowaj to, co w slocie.
          case 'destroy':
          case 'keep_current':
            player.destroyed.push(artifactId);
            console.log(`[LEGENDARY] Discarded new ${artifactId}${liveOccupant ? `, kept ${liveOccupant}` : ''}`);
            return {
              success: true,
              details: {
                action: liveOccupant ? 'kept_current' : 'destroyed',
                destroyed: artifactId,
                kept: liveOccupant || undefined
              }
            };

          default:
            return { success: false, details: { reason: 'Invalid choice for LEGENDARY_ARTIFACT_ACQUISITION' } };
        }
      }

      case EffectTypes.END_GAME_PENALTY:
        // Kara na koniec gry (np. -3 Sławy za każdą Ranę)
        // Zazwyczaj przetwarzane przy obliczaniu wyniku końcowego
        player.endGamePenalties = player.endGamePenalties || [];
        player.endGamePenalties.push({ type: 'fame', value: -value });
        return { success: true, details: { penalty: value } };

      // ========================================
      // EFEKTY ARTEFAKTÓW
      // ========================================

      case 'PEEK_AND_DISCARD_DECK':
      case EffectTypes.PEEK_AND_DISCARD_DECK: {
        // Podejrzyj wierzchnią kartę talii, opcjonalnie odrzuć na spód
        // Jeśli talia pusta - przetasuj odrzut
        if ((!player.deck || player.deck.length === 0) && player.discard && player.discard.length > 0) {
          console.log(`[PEEK_AND_DISCARD_DECK] Talia pusta - przetasowanie ${player.discard.length} kart z odrzutu`);
          const shuffled = player.shuffleArray([...player.discard]);
          player.deck.splice(0, player.deck.length, ...shuffled);
          player.discard.splice(0, player.discard.length);
        }
        if (!player.deck || player.deck.length === 0) {
          return { success: true, details: { peeked: null, reason: 'Deck and discard are empty' } };
        }
        const topCard = player.deck[0];

        // Gracz wybrał odrzucenie - przenieś na spód talii
        if (context.discard === true || context.choice === 'discard') {
          player.deck.shift();
          player.deck.push(topCard);
          return { success: true, details: { peeked: topCard, discarded: true } };
        }

        // Gracz wybrał zostawienie na wierzchu
        if (context.choice === 'keep') {
          return { success: true, details: { peeked: topCard, discarded: false } };
        }

        // Domyślnie tylko podgląd
        return { success: true, details: { peeked: topCard, discarded: false, needsChoice: true } };
      }

      case 'GAIN_RESOURCES':
      case EffectTypes.GAIN_RESOURCES: {
        // Zyskaj wiele typów zasobów naraz (np. Serce Smoka: +2 Siły, +2 Złota, +1 Wiedzy)
        // Przywilej Królewski: +2 Złota, +1 Zakup
        const gained = {};

        // Pobierz wartości z efektu lub z value (jeśli to obiekt)
        const effectValue = (typeof value === 'object' && value !== null) ? value : {};
        const strengthVal = effect.strength || effectValue.strength || 0;
        const knowledgeVal = effect.knowledge || effectValue.knowledge || 0;
        const goldVal = effect.gold || effectValue.gold || 0;
        const purchasesVal = effect.purchases || effectValue.purchases || 0;
        // Dobranie kart — Dary kart Szlaku, których róg pokazuje symbol dobierania
        const cardsVal = effect.cards || effectValue.cards || 0;

        if (strengthVal > 0) {
          ResourceManager.addToPool(player, { strength: strengthVal });
          gained.strength = strengthVal;
        }
        if (knowledgeVal > 0) {
          ResourceManager.addToPool(player, { knowledge: knowledgeVal });
          gained.knowledge = knowledgeVal;
        }
        if (goldVal > 0) {
          ResourceManager.addToPool(player, { gold: goldVal });
          gained.gold = goldVal;
        }
        if (purchasesVal > 0) {
          player.extraPurchases = (player.extraPurchases || 0) + purchasesVal;
          gained.purchases = purchasesVal;
        }
        if (cardsVal > 0 && player.drawCards) {
          player.drawCards(cardsVal);
          gained.cards = cardsVal;
        }
        console.log(`[CardEffects] GAIN_RESOURCES applied:`, gained);
        return { success: true, details: { ...gained, addedToPool: true } };
      }

      case 'STAT_BONUS':
      case EffectTypes.STAT_BONUS: {
        // Stałe bonusy do statystyk (obsługiwane przez ArtifactEffectProcessor.getAlwaysEffectBonuses)
        // Ten case jest dla kompatybilności - faktyczne bonusy są dodawane przy obliczaniu statystyk
        return { success: true, details: { message: 'Stat bonus applied passively', bonuses: { strength: effect.strength, knowledge: effect.knowledge, gold: effect.gold, cards: effect.cards } } };
      }

      case 'EXTRA_MAIN_ACTION':
      case EffectTypes.EXTRA_MAIN_ACTION: {
        // Dodatkowa akcja główna
        player.mainActionsRemaining = (player.mainActionsRemaining || 1) + (value || 1);
        return { success: true, details: { mainActions: value || 1 } };
      }

      case 'EXTRA_CHALLENGE_ACTION':
      case EffectTypes.EXTRA_CHALLENGE_ACTION: {
        // Dodatkowa akcja tylko na wyzwanie
        player.extraChallenges = (player.extraChallenges || 0) + (value || 1);
        return { success: true, details: { extraChallenges: value || 1 } };
      }

      case 'DRAW_CARD':
      case EffectTypes.DRAW_CARD: {
        // Dobierz kartę (alias dla DRAW_CARDS z value=1)
        if (player.drawCards) {
          const drawn = player.drawCards(value || 1);
          return { success: true, details: { drawn: drawn.length, cards: drawn } };
        }
        return { success: false, details: { reason: 'drawCards method not available' } };
      }

      case 'FREE_MARKET_CARD':
      case EffectTypes.FREE_MARKET_CARD: {
        // Darmowa karta z targowiska (po pokonaniu potwora)
        // Wymaga wyboru karty przez gracza
        if (context.cardId && game.marketplace) {
          const maxCost = context.maxCost || Infinity;
          const idx = game.marketplace.indexOf(context.cardId);
          if (idx !== -1) {
            // TODO: Sprawdź koszt karty
            game.marketplace[idx] = null;   // pusty slot zostaje na miejscu, refill wypełni go w tym samym polu
            player.discard.push(context.cardId);
            if (game.refillMarketplace) game.refillMarketplace();
            return { success: true, details: { cardId: context.cardId, free: true } };
          }
        }
        return { success: false, details: { reason: 'No card selected or not in marketplace' } };
      }

      case 'GAIN_GOLD_PER_TROPHY':
      case EffectTypes.GAIN_GOLD_PER_TROPHY: {
        // +1 Złota za każde 2 trofea (max +6)
        const trophyCount = player.trophies ? player.trophies.length : 0;
        const goldGain = Math.min(6, Math.floor(trophyCount / 2));
        ResourceManager.addToPool(player, { gold: goldGain });
        return { success: true, details: { trophies: trophyCount, gold: goldGain, addedToPool: true } };
      }

      case 'GAIN_ANY_RESOURCE':
      case EffectTypes.GAIN_ANY_RESOURCE: {
        // Zyskaj dowolny zasób (wymaga wyboru gracza)
        const amount = value || 2;
        if (context.choice) {
          // context.choice może być 'strength', 'knowledge', 'gold' lub obiekt {strength: 1, knowledge: 1}
          if (typeof context.choice === 'string') {
            ResourceManager.addToPool(player, { [context.choice]: amount });
            return { success: true, details: { [context.choice]: amount, addedToPool: true } };
          } else if (typeof context.choice === 'object') {
            ResourceManager.addToPool(player, context.choice);
            return { success: true, details: { ...context.choice, addedToPool: true } };
          }
        }
        // Wymaga wyboru
        return { success: true, details: { amount, needsChoice: true, options: ['strength', 'knowledge', 'gold'] } };
      }

      case 'GAIN_GOLD_CONDITIONAL':
      case EffectTypes.GAIN_GOLD_CONDITIONAL: {
        // +1 Złota, +1 Zakup jeśli masz 10+ złota (Suknie Bogactwa)
        ResourceManager.addToPool(player, { gold: 1 });
        const totalGold = (player.pool?.gold || 0) + (player.resources?.gold || 0);
        let bonusPurchase = false;
        if (totalGold >= 10) {
          player.extraPurchases = (player.extraPurchases || 0) + 1;
          bonusPurchase = true;
        }
        return { success: true, details: { gold: 1, bonusPurchase, totalGold, addedToPool: true } };
      }

      case 'CONVERT_RESOURCES':
      case EffectTypes.CONVERT_RESOURCES: {
        // Konwersja zasobów 1:1 (Kamień Filozoficzny)
        // context: { from: 'strength', to: 'gold', amount: 3 }
        if (context.from && context.to && context.amount) {
          const { from, to, amount } = context;
          const available = player.pool?.[from] || 0;
          const actualAmount = Math.min(amount, available);
          if (actualAmount > 0) {
            ResourceManager.removeFromPool(player, { [from]: actualAmount });
            ResourceManager.addToPool(player, { [to]: actualAmount });
            return { success: true, details: { from, to, amount: actualAmount } };
          }
        }
        return { success: false, details: { reason: 'Invalid conversion parameters or insufficient resources' } };
      }

      case 'PAY_FAME_DRAW_CARDS':
      case EffectTypes.PAY_FAME_DRAW_CARDS: {
        // Zapłać sławę za karty (Całun Wskrzesiciela)
        // context: { fameToPay: 2 }
        const fameToPay = context.fameToPay || value || 1;
        if (player.fame >= fameToPay) {
          player.fame -= fameToPay;
          if (player.drawCards) {
            const drawn = player.drawCards(fameToPay);
            return { success: true, details: { famePaid: fameToPay, cardsDrawn: drawn.length } };
          }
        }
        return { success: false, details: { reason: 'Not enough fame or drawCards not available' } };
      }

      case 'PLAY_FROM_DISCARD':
      case EffectTypes.PLAY_FROM_DISCARD: {
        // Zagraj kartę z odrzutu (Pieczęć Króla-Czarnoksiężnika)
        // context: { cardId: 'ally_xxx' }
        if (context.cardId && player.discard) {
          const idx = player.discard.indexOf(context.cardId);
          if (idx !== -1) {
            player.discard.splice(idx, 1);
            player.hand.push(context.cardId);
            return { success: true, details: { cardId: context.cardId, addedToHand: true } };
          }
        }
        return { success: false, details: { reason: 'Card not in discard' } };
      }

      case 'REFRESH_MARKETPLACE_CARD':
      case 'REPLACE_MARKET_CARD':
      case EffectTypes.REPLACE_MARKET_CARD: {
        // Wymień kartę na targowisku (Samira's ability / Oko Proroka)
        if (context.cardId && game.marketplace) {
          const idx = game.marketplace.indexOf(context.cardId);
          if (idx !== -1) {
            const removed = game.marketplace[idx];
            game.marketplace[idx] = null;   // pusty slot zostaje na miejscu
            game.marketDiscard = game.marketDiscard || [];
            game.marketDiscard.push(removed);
            if (game.refillMarketplace) game.refillMarketplace();
            return { success: true, details: { removed, replaced: true } };
          }
        }
        return { success: false, details: { reason: 'Card not in marketplace' } };
      }

      case 'RECOVER_FROM_DISCARD':
      case EffectTypes.RECOVER_FROM_DISCARD: {
        // Odzyskaj kartę z odrzutu na wierzch talii
        if (context.cardId && player.discard) {
          const idx = player.discard.indexOf(context.cardId);
          if (idx !== -1) {
            player.discard.splice(idx, 1);
            player.deck.unshift(context.cardId);
            return { success: true, details: { cardId: context.cardId, movedToTopOfDeck: true } };
          }
        }
        return { success: false, details: { reason: 'Card not in discard' } };
      }

      case 'IGNORE_PENALTY':
      case EffectTypes.IGNORE_PENALTY: {
        // Ignoruj karę z przygody (jednorazowo)
        player.ignorePenalty = true;
        return { success: true, details: { ignorePenalty: true } };
      }

      case 'PAY_FOR_EXTRA_CHALLENGE':
      case EffectTypes.PAY_FOR_EXTRA_CHALLENGE: {
        // Zapłać złoto za dodatkowe wyzwanie
        const cost = value || 3;
        const poolGold = player.pool?.gold || 0;
        if (poolGold >= cost) {
          ResourceManager.removeFromPool(player, { gold: cost });
          player.challengesRemaining = (player.challengesRemaining || 0) + 1;
          return { success: true, details: { goldPaid: cost, challengeAdded: true } };
        }
        return { success: false, details: { reason: `Not enough gold (need ${cost}, have ${poolGold})` } };
      }

      case 'SCRY_ADVENTURE_DECK':
      case EffectTypes.SCRY_ADVENTURE_DECK: {
        // Podejrzyj 3 karty z talii przygód, przeorganizuj
        const deck = game.adventureDeck || game.wisdomDeck || game.courageDeck;
        if (deck && deck.length > 0) {
          const count = Math.min(3, deck.length);
          const peeked = deck.slice(0, count);
          // Jeśli gracz podał nową kolejność
          if (context.order && Array.isArray(context.order)) {
            const top3 = deck.splice(0, count);
            for (const cardId of context.order.reverse()) {
              if (top3.includes(cardId)) {
                deck.unshift(cardId);
              }
            }
            // Jeśli gracz wybrał kartę na spód
            if (context.toBottom) {
              const card = deck.shift();
              deck.push(card);
            }
            return { success: true, details: { arranged: context.order, toBottom: context.toBottom } };
          }
          return { success: true, details: { peeked, needsOrder: true } };
        }
        return { success: false, details: { reason: 'Adventure deck not available' } };
      }

      case 'IMMUNITY':
      case EffectTypes.IMMUNITY: {
        // Odporność na kary (obsługiwane pasywnie)
        player.hasImmunity = true;
        return { success: true, details: { immunity: true } };
      }

      case 'BONUS_KNOWLEDGE':
      case EffectTypes.BONUS_KNOWLEDGE: {
        // Bonus do wiedzy podczas wyzwań
        player.knowledgeBonus = (player.knowledgeBonus || 0) + (value || 3);
        return { success: true, details: { knowledgeBonus: value || 3 } };
      }

      case 'INCREASE_HAND_SIZE':
      case EffectTypes.INCREASE_HAND_SIZE: {
        // Zwiększ rozmiar ręki
        player.handSize = (player.handSize || 5) + (value || 1);
        return { success: true, details: { newHandSize: player.handSize } };
      }

      case 'COPY_EFFECT':
      case EffectTypes.COPY_EFFECT: {
        // Kopiuj efekt karty (Lustro Iluzji)
        // Obsługiwane w logice zagrywania kart
        player.copyNextEffect = true;
        return { success: true, details: { copyNextEffect: true } };
      }

      case 'STRENGTH_PER_ARTIFACT':
      case EffectTypes.STRENGTH_PER_ARTIFACT: {
        // Młot Kowadła Świata: +1 Siły za każdy Artefakt w grze (Targ + założone u graczy).
        // Lustro backendowego CardEffects — patrz komentarz tam.
        let artifactCount = 0;
        for (const cardId of game?.marketplace || []) {
          if (!cardId) continue;
          const marketCard = await GameEngine.getCardById(cardId);
          if (marketCard?.type === 'artifact') artifactCount++;
        }
        for (const p of game?.players || []) {
          for (const slot of ['pancerz', 'bron', 'amulet']) {
            if (p.equippedArtifacts?.[slot]) artifactCount++;
          }
        }
        if (artifactCount > 0) {
          ResourceManager.addToPool(player, { strength: artifactCount });
        }
        return { success: true, details: { artifacts: artifactCount, strength: artifactCount, addedToPool: true } };
      }

      case 'DESTROY_FOR_GOLD':
      case EffectTypes.DESTROY_FOR_GOLD: {
        // Zniszcz kartę za złoto
        if (context.cardId) {
          const goldValue = value || 2;
          console.log(`[DESTROY_FOR_GOLD] Starting for card ${context.cardId}, value: ${goldValue}`);
          console.log(`[DESTROY_FOR_GOLD] Player state before: Pool Gold=${player.pool?.gold}, Bank Gold=${player.resources?.gold}`);

          // Najpierw sprawdź odrzut (preferowane dla Rogu Obfitości)
          let idx = player.discard.indexOf(context.cardId);
          if (idx !== -1) {
            player.discard.splice(idx, 1);
            player.destroyed = player.destroyed || [];
            player.destroyed.push(context.cardId);
            ResourceManager.addToPool(player, { gold: goldValue });
            player.cardsDestroyedThisTurn = (player.cardsDestroyedThisTurn || 0) + 1;

            console.log(`[DESTROY_FOR_GOLD] Success from discard. New Pool Gold: ${player.pool?.gold}`);
            return { success: true, details: { destroyed: context.cardId, goldGained: goldValue, from: 'discard' } };
          }

          // Potem sprawdź rękę
          idx = player.hand.indexOf(context.cardId);
          if (idx !== -1) {
            player.hand.splice(idx, 1);
            player.destroyed = player.destroyed || [];
            player.destroyed.push(context.cardId);
            ResourceManager.addToPool(player, { gold: goldValue });
            player.cardsDestroyedThisTurn = (player.cardsDestroyedThisTurn || 0) + 1;

            console.log(`[DESTROY_FOR_GOLD] Success from hand. New Pool Gold: ${player.pool?.gold}`);
            return { success: true, details: { destroyed: context.cardId, goldGained: goldValue, from: 'hand' } };
          }
        }
        return { success: false, details: { reason: 'Card not found in hand or discard' } };
      }

      // ========================================
      // EFEKTY BOHATERÓW (HERO ABILITIES)
      // ========================================

      case 'GAIN_STRENGTH_1':
        // Borin Stonefist - +1 Siły (permanent, do banku)
        ResourceManager.addToPool(player, { strength: 1 });
        console.log(`[CardEffects] GAIN_STRENGTH_1: +1 Strength to pool`);
        return { success: true, details: { strength: 1, addedToPool: true } };

      case 'GAIN_STRENGTH_2_TEMPORARY':
        // Bronwyn Wildheart / Khela Skullsplitter - +2 Siły tymczasowe (do puli)
        if (!player.pool) player.pool = { strength: 0, knowledge: 0, gold: 0 };
        ResourceManager.addToPool(player, { strength: 2 });
        console.log(`[CardEffects] GAIN_STRENGTH_2_TEMPORARY: +2 Strength to pool`);
        return { success: true, details: { temporaryStrength: 2, addedToPool: true } };

      case 'GAIN_KNOWLEDGE_2_TEMPORARY':
        // Elowen Moonsong - +2 Wiedzy tymczasowe (do puli)
        if (!player.pool) player.pool = { strength: 0, knowledge: 0, gold: 0 };
        ResourceManager.addToPool(player, { knowledge: 2 });
        console.log(`[CardEffects] GAIN_KNOWLEDGE_2_TEMPORARY: +2 Knowledge to pool`);
        return { success: true, details: { temporaryKnowledge: 2, addedToPool: true } };

      case 'GAIN_2_STRENGTH_OR_2_KNOWLEDGE_TEMPORARY':
        // Torbin Geargrind / Faolan the Claw - +2 Siły LUB +2 Wiedzy
        if (!player.pool) player.pool = { strength: 0, knowledge: 0, gold: 0 };
        if (context.choice === 'strength') {
          ResourceManager.addToPool(player, { strength: 2 });
          console.log(`[CardEffects] GAIN_2_STRENGTH_OR_2_KNOWLEDGE_TEMPORARY: chose +2 Strength`);
          return { success: true, details: { choice: 'strength', temporaryStrength: 2, addedToPool: true } };
        } else if (context.choice === 'knowledge') {
          ResourceManager.addToPool(player, { knowledge: 2 });
          console.log(`[CardEffects] GAIN_2_STRENGTH_OR_2_KNOWLEDGE_TEMPORARY: chose +2 Knowledge`);
          return { success: true, details: { choice: 'knowledge', temporaryKnowledge: 2, addedToPool: true } };
        }
        return { success: false, details: { reason: 'No choice provided for GAIN_2_STRENGTH_OR_2_KNOWLEDGE_TEMPORARY' } };

      case 'GAIN_4_GOLD_OR_3_STRENGTH_TEMPORARY':
        // Drakon Embermaw - +4 Złota LUB +3 Siły
        if (!player.pool) player.pool = { strength: 0, knowledge: 0, gold: 0 };
        if (context.choice === 'gold') {
          ResourceManager.addToPool(player, { gold: 4 });
          console.log(`[CardEffects] GAIN_4_GOLD_OR_3_STRENGTH_TEMPORARY: chose +4 Gold`);
          return { success: true, details: { choice: 'gold', gold: 4, addedToPool: true } };
        } else if (context.choice === 'strength') {
          ResourceManager.addToPool(player, { strength: 3 });
          console.log(`[CardEffects] GAIN_4_GOLD_OR_3_STRENGTH_TEMPORARY: chose +3 Strength`);
          return { success: true, details: { choice: 'strength', temporaryStrength: 3, addedToPool: true } };
        }
        return { success: false, details: { reason: 'No choice provided for GAIN_4_GOLD_OR_3_STRENGTH_TEMPORARY' } };

      case 'DESTROY_1_FROM_DISCARD':
        // Lucian Ashborn - Zniszcz 1 kartę ze stosu odrzuconych
        if (context.cardId) {
          const idx = player.discard.indexOf(context.cardId);
          if (idx !== -1) {
            player.discard.splice(idx, 1);
            player.destroyed = player.destroyed || [];
            player.destroyed.push(context.cardId);
            console.log(`[CardEffects] DESTROY_1_FROM_DISCARD: destroyed ${context.cardId}`);
            return { success: true, details: { destroyed: context.cardId } };
          }
        }
        return { success: false, details: { reason: 'Card not in discard pile' } };

      case 'ARTIFACT_STAT_BONUS_CHOICE':
        // Wybór bonusu z artefaktu (np. Hełm Dowódcy: +3 Siła LUB +3 Wiedza LUB +3 Złoto)
        if (!player.pool) player.pool = { strength: 0, knowledge: 0, gold: 0 };

        // effect tutaj to pendingEffect z bazy, więc ma pola strength/knowledge/gold
        const bonusStrength = effect.strength || 0;
        const bonusKnowledge = effect.knowledge || 0;
        const bonusGold = effect.gold || 0;

        if (context.choice === 'strength' && bonusStrength > 0) {
          ResourceManager.addToPool(player, { strength: bonusStrength });
          console.log(`[CardEffects] ARTIFACT_STAT_BONUS_CHOICE: chose +${bonusStrength} Strength`);
          return { success: true, details: { choice: 'strength', strength: bonusStrength, addedToPool: true } };
        } else if (context.choice === 'knowledge' && bonusKnowledge > 0) {
          ResourceManager.addToPool(player, { knowledge: bonusKnowledge });
          console.log(`[CardEffects] ARTIFACT_STAT_BONUS_CHOICE: chose +${bonusKnowledge} Knowledge`);
          return { success: true, details: { choice: 'knowledge', knowledge: bonusKnowledge, addedToPool: true } };
        } else if (context.choice === 'gold' && bonusGold > 0) {
          ResourceManager.addToPool(player, { gold: bonusGold });
          console.log(`[CardEffects] ARTIFACT_STAT_BONUS_CHOICE: chose +${bonusGold} Gold`);
          return { success: true, details: { choice: 'gold', gold: bonusGold, addedToPool: true } };
        }
        return { success: false, details: { reason: 'Invalid choice for ARTIFACT_STAT_BONUS_CHOICE' } };

      // ========================================
      // DEFAULT
      // ========================================

      default:
        console.warn(`Unknown effect type: ${type}`);
        return { success: false, details: { reason: `Unknown effect type: ${type}` } };
    }
  }

  /**
   * Potasuj tablicę (Fisher-Yates shuffle)
   * @param {Array} array - Tablica do potasowania
   */
  static shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  /**
   * Sprawdź czy efekt wymaga wyboru gracza
   * @param {String} effectType - Typ efektu
   * @returns {Boolean}
   */
  static requiresPlayerChoice(effectType) {
    const choiceEffects = [
      'ALL_PLAYERS_CHOICE_DISCARD_2_OR_TAKE_WOUND',
      EffectTypes.CHOICE_GAIN_GOLD_2_OR_GAIN_GOLD_3_TAKE_SHAME,
      EffectTypes.CHOICE_GAIN_STRENGTH_2_OR_GAIN_KNOWLEDGE_2,
      'CHOICE_GAIN_STRENGTH_1_OR_GAIN_KNOWLEDGE_1', // Znak Natury
      'CHOICE_GAIN_STRENGTH_8_OR_GOLD_8_OR_KNOWLEDGE_8', // Aspekt Bohatera
      'GAIN_2_STRENGTH_OR_2_KNOWLEDGE_TEMPORARY', // Torbin Geargrind / Faolan
      'GAIN_4_GOLD_OR_3_STRENGTH_TEMPORARY', // Drakon Embermaw
      'DESTROY_1_FROM_DISCARD', // Lucian Ashborn
      EffectTypes.DISCARD_FROM_HAND,
      EffectTypes.DISCARD_ARTIFACT,
      EffectTypes.DISCARD_MARKET_CARD,
      EffectTypes.DISCARD_FROM_KNOWN_PATH,
      EffectTypes.DESTROY_FROM_HAND,
      EffectTypes.DESTROY_FROM_DISCARD,
      EffectTypes.DESTROY_FROM_HAND_OR_DISCARD,
      EffectTypes.DESTROY_FROM_HAND_OPTIONAL,
      EffectTypes.OPTIONAL_DESTROY_FROM_HAND,
      EffectTypes.DESTROY_CARD_FROM_HAND,
      EffectTypes.DESTROY_ALLY_FROM_HAND,
      EffectTypes.DESTROY_MARKET_CARD,
      EffectTypes.DESTROY_EQUIPPED_ARTIFACT,
      EffectTypes.TAKE_FREE_MARKET_CARD,
      EffectTypes.TAKE_MARKET_CARD_COST_5_OR_LESS,
      EffectTypes.MOVE_MARKET_CARD_TO_BOTTOM,
      EffectTypes.PLACE_FROM_HAND_ON_TOP_OF_DECK,
      EffectTypes.PLACE_ALLY_ON_TOP_FROM_DISCARD,
      EffectTypes.RECOVER_MANEUVER_FROM_DISCARD,
      EffectTypes.TAKE_ALLY_FROM_MARKET_TO_TOP,
      EffectTypes.PEEK_TOP_3_PLACE_IN_ORDER,
      // Efekty artefaktów wymagające wyboru
      'PEEK_AND_DISCARD_DECK',
      'GAIN_ANY_RESOURCE',
      'CONVERT_RESOURCES',
      'PAY_FAME_DRAW_CARDS',
      'PLAY_FROM_DISCARD',
      'FREE_MARKET_CARD',
      'REPLACE_MARKET_CARD',
      'REFRESH_MARKETPLACE_CARD',
      'RECOVER_FROM_DISCARD',
      'PAY_FOR_EXTRA_CHALLENGE',
      'SCRY_ADVENTURE_DECK',
      'DESTROY_FOR_GOLD',
      'LEGENDARY_ARTIFACT_ACQUISITION',
      EffectTypes.LEGENDARY_ARTIFACT_ACQUISITION,
      // Nowy efekt wyboru bonusu z artefaktu
      'ARTIFACT_STAT_BONUS_CHOICE',
      // Opcjonalna wymiana całego targowiska
      EffectTypes.REPLACE_ALL_MARKET_CARDS,
      // Podejrzenie kart z talii przygód (dwufazowe)
      EffectTypes.PEEK_ADVENTURE_DECK,
      // Odrzucanie kart z ręki za siłę (Ścieżka Wojny)
      EffectTypes.DISCARD_FROM_HAND_FOR_STRENGTH,
    ];

    return choiceEffects.includes(effectType);
  }

  /**
   * Pobierz opcje wyboru dla efektu
   * @param {String} effectType - Typ efektu
   * @returns {Array} - Opcje wyboru
   */
  static getChoiceOptions(effectType) {
    switch (effectType) {
      case 'ALL_PLAYERS_CHOICE_DISCARD_2_OR_TAKE_WOUND':
        return [
          { id: 'discard_2', label: 'Odrzuć 2 karty' },
          { id: 'take_wound', label: 'Dobierz kartę Rany' }
        ];

      case EffectTypes.CHOICE_GAIN_GOLD_2_OR_GAIN_GOLD_3_TAKE_SHAME:
        return [
          { id: 'option1', label: '+2 Złoto' },
          { id: 'option2', label: '+3 Złoto + Hańba' }
        ];

      case EffectTypes.CHOICE_GAIN_STRENGTH_2_OR_GAIN_KNOWLEDGE_2:
        return [
          { id: 'strength', label: '+2 Siła' },
          { id: 'knowledge', label: '+2 Wiedza' }
        ];

      case 'CHOICE_GAIN_STRENGTH_1_OR_GAIN_KNOWLEDGE_1':
        return [
          { id: 'strength', label: '+1 Siła' },
          { id: 'knowledge', label: '+1 Wiedza' }
        ];

      case 'CHOICE_GAIN_STRENGTH_8_OR_GOLD_8_OR_KNOWLEDGE_8':
        return [
          { id: 'strength', label: '+8 Siła' },
          { id: 'gold', label: '+8 Złoto' },
          { id: 'knowledge', label: '+8 Wiedza' }
        ];

      case 'GAIN_2_STRENGTH_OR_2_KNOWLEDGE_TEMPORARY':
        return [
          { id: 'strength', label: '+2 Siła (Tymczasowo)' },
          { id: 'knowledge', label: '+2 Wiedza (Tymczasowo)' }
        ];

      case 'GAIN_4_GOLD_OR_3_STRENGTH_TEMPORARY':
        return [
          { id: 'gold', label: '+4 Złota' },
          { id: 'strength', label: '+3 Siła (Tymczasowo)' }
        ];

      case 'ARTIFACT_STAT_BONUS_CHOICE':
        // Opcje są budowane dynamicznie w handleActivateArtifact
        // Tu zwracamy wszystkie możliwe opcje
        return [
          { id: 'strength', label: 'Siła' },
          { id: 'knowledge', label: 'Wiedza' },
          { id: 'gold', label: 'Złoto' }
        ];

      case EffectTypes.REPLACE_ALL_MARKET_CARDS:
        return [
          { id: 'replace', label: 'Wymień wszystkie karty' },
          { id: 'skip', label: 'Pomiń' }
        ];

      default:
        return [];
    }
  }
}

export default CardEffects;
